import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  FileUp,
  Download,
  Eye,
  Edit,
  Upload,
  Check,
  X,
  Settings,
  Filter,
  MoreVertical,
  LayoutGrid,
  List,
  Info,
} from "lucide-react";
import * as XLSX from "xlsx";
import toast from 'react-hot-toast';
import InvestmentDeclaration from "./InvestmentDeclaration";
import InvestmentDeclarationModal from "./tax-planning/TaxPlanningModal";
import { PayrollSettingsPage } from "./payroll-settings";
import PayrollReports from "./PayrollReports";
import Reports from "./Reports";
import UnpaidSalaryReportPanel from "./payroll/UnpaidSalaryReportPanel";
import AdminFlexiClaimsPanel from "./payroll/AdminFlexiClaimsPanel";
import StandardFilter from "../Common/StandardFilter";
import { DsButton, DsIcon } from "../../design-system";
import ActionInsightsPanel from "./ActionInsightsPanel";
import AttachmentPreviewModal from "../Common/AttachmentPreviewModal";
import {
  employeeService,
  departmentService,
  designationService,
  branchService,
  gradesService,
  categoryService,
  subDepartmentService,
  payrollService,
  salaryStructureService,
  salaryService,
  payrollOpsService,
  API_URL,
} from "../../services/api";
import api from "../../services/api";

import Filters from "./Filters";
import DatePicker from "../Common/DatePicker";
import { format } from "date-fns";
import { debounce } from "lodash";
import { ADMIN_SHELL_NAV_EVENT } from "../../utils/adminShellNav";
import { usePermissions } from "../../rbac/PermissionsContext";
import {
  PAYROLL_STEP_RBAC,
  canApproveProcessStep,
} from "../../rbac/processStepAccess";
import {
  templatesToStructures,
  buildPayrollBreakdownColumnKeys,
  enrichEmployeesWithSalaryBreakdown,
  formatPayrollAmount,
} from "../../utils/payrollSalaryBreakdownEnrichment";
import { getAuthJson } from "../../utils/authStorage";
import { flattenEmployeeList } from "../../utils/displayLabel";
import UpdateStatutoryApplicabilityModal from "./payroll/UpdateStatutoryApplicabilityModal";
import PayrollSalaryRevisionModal from "./payroll/PayrollSalaryRevisionModal";
import SalaryRevisionImportModal from "./payroll/SalaryRevisionImportModal";
import StructureComponentImportModal from "./payroll/StructureComponentImportModal";
import LoanEmployeeModal, {
  LeaveEncashmentEmployeeModal,
} from "./payroll/loan/LoanEmployeeModal";
import RunPayrollWizard from "./payroll/RunPayrollWizard";
import PayrollActionsHub from "./payroll/PayrollActionsHub";
import EmployeeSalaryStructureModal from "./employee-management/salary-structure/EmployeeSalaryStructureModal";
import PayrollChecklistEditor from "./payroll-settings/PayrollChecklistEditor";
import PayrollDashboardPanel from "./payroll/PayrollDashboardPanel";
import PayrollStatutoryFilingPanel from "./payroll/PayrollStatutoryFilingPanel";
import ExcelJS from "exceljs";

const getMonthRangeValue = (year, month) => {
  const paddedMonth = (month + 1).toString().padStart(2, "0");
  const start = `${year}-${paddedMonth}-01`;
  const endDay = new Date(year, month + 1, 0)
    .getDate()
    .toString()
    .padStart(2, "0");
  const end = `${year}-${paddedMonth}-${endDay}`;
  return `${start}/${end}`;
};

const getFinancialYearFromCalendarMonth = (year, month) => {
  if (month >= 3) {
    const end = (year + 1) % 100;
    return `${year}-${String(end).padStart(2, "0")}`;
  }
  const end = year % 100;
  return `${year - 1}-${String(end).padStart(2, "0")}`;
};

const toDateOnlyYmd = (raw) => {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const mo = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
};

/** Effective LWD for payroll month scope — prefer relieved/LWD; resignation only if already left. */
const getEffectiveRelievingYmd = (employee) => {
  if (!employee) return null;
  const primary = toDateOnlyYmd(
    employee.relievedDate ||
      employee.lastWorkingDay ||
      employee.leaveDate ||
      employee.dateOfLeaving
  );
  if (primary) return primary;
  const status = String(employee.employmentStatus || "").trim().toLowerCase();
  const left =
    status === "relieved" || status === "resigned" || status === "terminated";
  if (!left) return null;
  return toDateOnlyYmd(
    employee.terminationDate ||
      employee.resignationDate ||
      employee.exitDate ||
      employee.endDate ||
      employee.separationDate
  );
};

/**
 * Keep employees employed during the calendar month:
 * - joined on/before month end
 * - LWD null OR LWD >= month start (leaving month inclusive; prior months excluded)
 */
const filterEmployeesByCalendarMonth = (employees, year, month) => {
  const mo = Number(month) + 1;
  const monthStart = `${year}-${String(mo).padStart(2, "0")}-01`;
  const lastDay = new Date(year, mo, 0).getDate();
  const monthEnd = `${year}-${String(mo).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

  return (employees || []).filter((employee) => {
    const joiningYmd = toDateOnlyYmd(
      employee.dateOfJoin || employee.joiningDate || employee.joinDate
    );
    if (joiningYmd && joiningYmd > monthEnd) {
      return false;
    }

    const relievingYmd = getEffectiveRelievingYmd(employee);
    if (relievingYmd && relievingYmd < monthStart) {
      return false;
    }

    return true;
  });
};

export default function Payroll({ darkMode, setCurrentPage, toggleDarkMode }) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  const navigate = useNavigate();
  const PAYROLL_INSIGHTS_KEY = "payrollActionInsightsLog";
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [designationsList, setDesignationsList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [gradesList, setGradesList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [subDepartmentsList, setSubDepartmentsList] = useState([]);
  const payrollMonthNow = new Date();
  const [selectedMonth, setSelectedMonth] = useState(payrollMonthNow.getMonth());
  const [selectedYear, setSelectedYear] = useState(payrollMonthNow.getFullYear());
  const [minDate, setMinDate] = useState(null);
  const [monthPickerValue, setMonthPickerValue] = useState(() =>
    getMonthRangeValue(payrollMonthNow.getFullYear(), payrollMonthNow.getMonth())
  );
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const selectedFinancialYear = useMemo(
    () => getFinancialYearFromCalendarMonth(selectedYear, selectedMonth),
    [selectedYear, selectedMonth]
  );

  const shouldShowIncomeTaxGuide = (() => {
    const fy = String(selectedFinancialYear || "").trim();
    const match = fy.match(/^(\d{4})-(\d{2}|\d{4})$/);
    if (!match) return false;
    const startYear = Number(match[1]);
    const endYearRaw = match[2];
    const endYear = endYearRaw.length === 2 ? Number(`${match[1].slice(0, 2)}${endYearRaw}`) : Number(endYearRaw);
    return startYear === 2026 && endYear === 2027;
  })();

  const formatDisplayDate = (value) => {
    if (!value || value === "-") return "-";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return format(d, "dd-MMM-yy");
    } catch (e) {
      return value;
    }
  };

  // Pagination and view mode states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState("list");
  const [sortField, setSortField] = useState("dateOfJoin");
  const [sortDirection, setSortDirection] = useState("asc");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Column width and row height customization
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [openActionDropdown, setOpenActionDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [actionEmployee, setActionEmployee] = useState(null);
  const actionButtonRefs = useRef({});
  // Stub kept so stale HMR patches that still reference bulkImportInputRef do not crash.
  const bulkImportInputRef = useRef(null);
  const [sidebarImportOpen, setSidebarImportOpen] = useState(false);
  const [salaryRevisionImportOpen, setSalaryRevisionImportOpen] = useState(false);
  const [structureComponentImportOpen, setStructureComponentImportOpen] = useState(false);
  const [statutoryFilingMenuOpen, setStatutoryFilingMenuOpen] = useState(false);
  const [activeStatutoryFilingSubTab, setActiveStatutoryFilingSubTab] = useState("pf");
  const [statutoryFilingSubTabTouched, setStatutoryFilingSubTabTouched] = useState(false);
  const [columnWidths, setColumnWidths] = useState({
    actions: 48,
    select: 40,
    slNo: 60,
    empCode: 120,
    name: 200,
    designation: 150,
    department: 150,
    joiningDate: 120,
    exitDate: 120,
    ctc: 120,
    salaryStructure: 160,
    taxRegime: 100,
    status: 120,
  });
  const [rowHeight, setRowHeight] = useState(40);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [statutoryConfigJson, setStatutoryConfigJson] = useState(null);
  const [orgSalaryMethod, setOrgSalaryMethod] = useState(
    () => getAuthJson("companyData")?.defaultSalaryStructureMethod || "CTC"
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalMode, setModalMode] = useState("view"); // 'view' or 'edit'

  // Upload and approval states
  const [photoModal, setPhotoModal] = useState({
    isOpen: false,
    photo: null,
    fileName: "",
    employeeId: null,
  });
  const [photoUploading, setPhotoUploading] = useState({});
  const [documentStatus, setDocumentStatus] = useState({}); // Track approve/reject status
  const [rejectReasons, setRejectReasons] = useState({}); // Track rejection reasons

  // Tab state for Payroll and Reports
  const [activePayrollTab, setActivePayrollTab] = useState(() => {
    try {
      const tab = new URLSearchParams(window.location.search).get("tab");
      const allowed = [
        "dashboard",
        "assigned_structure",
        "checklist_tasks",
        "run_payroll",
        "reports",
        "statutory_filing",
        "settings",
        "payroll",
      ];
      if (tab && allowed.includes(tab)) {
        if (tab === "assigned_structure" || tab === "payroll") return "run_payroll";
        return tab;
      }
    } catch {
      /* ignore */
    }
    return "dashboard";
  });
  const [showPayrollInsights, setShowPayrollInsights] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectStatutoryFilingSubTab = useCallback(
    (subId) => {
      setActiveStatutoryFilingSubTab(subId);
      setStatutoryFilingSubTabTouched(true);
      setStatutoryFilingMenuOpen(true);
      const next = new URLSearchParams(searchParams);
      next.set("tab", "statutory_filing");
      next.set("sub", subId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const tab = searchParams.get("tab");
    const sub = searchParams.get("sub");
    const allowed = [
      "dashboard",
      "assigned_structure",
      "checklist_tasks",
      "run_payroll",
      "reports",
      "statutory_filing",
      "settings",
      "payroll",
    ];
    if (tab && allowed.includes(tab)) {
      // Assigned Structure moved into Run Payroll → Actions
      if (tab === "assigned_structure" || tab === "payroll") {
        setActivePayrollTab("run_payroll");
        setRunPayrollPanelView("actions");
      } else {
        setActivePayrollTab(tab);
      }
      setShowPayrollInsights(false);
    }
    if (tab === "run_payroll") {
      const view = searchParams.get("view");
      if (view === "run" || view === "process") setRunPayrollPanelView("run");
      else if (view === "actions") setRunPayrollPanelView("actions");
    }
    if (tab === "statutory_filing") {
      const statutorySubs = ["pf", "esi", "pt", "nps", "meal", "form-24q"];
      if (sub && statutorySubs.includes(sub)) {
        setActiveStatutoryFilingSubTab(sub);
      } else {
        setActiveStatutoryFilingSubTab((cur) =>
          statutorySubs.includes(cur) ? cur : "pf"
        );
      }
      setStatutoryFilingSubTabTouched(true);
      setStatutoryFilingMenuOpen(true);
    }
    if (tab === "reports" && (sub === "salary-revision" || sub === "form-16" || sub === "unpaid-salary")) {
      setActivePayrollReportsSubTab(sub);
      setPayrollReportsSubTabTouched(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const onShellNav = (event) => {
      if (event?.detail?.module !== "payroll") return;
      const tab = event?.detail?.tab;
      if (!tab) return;
      setActivePayrollTab(tab);
      setShowPayrollInsights(false);
      if (tab === "statutory_filing") {
        const sub = event?.detail?.sub;
        const statutorySubs = ["pf", "esi", "pt", "form-24q"];
        if (sub && statutorySubs.includes(sub)) {
          setActiveStatutoryFilingSubTab(sub);
        }
        setStatutoryFilingSubTabTouched(true);
        setStatutoryFilingMenuOpen(true);
      }
    };
    window.addEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
    return () => window.removeEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
  }, []);

  const [selectedStructureFilterIds, setSelectedStructureFilterIds] = useState([]); // empty = all
  const [payrollSelectedEmployeeIds, setPayrollSelectedEmployeeIds] = useState([]);
  const [structureFilterOpen, setStructureFilterOpen] = useState(false);
  const [statutoryOverridesByEmployeeId, setStatutoryOverridesByEmployeeId] = useState(
    () => new Map()
  );
  const [revisionEmployee, setRevisionEmployee] = useState(null);
  const [loanEmployee, setLoanEmployee] = useState(null);
  const [flexiClaimsOpen, setFlexiClaimsOpen] = useState(false);
  const [encashEmployee, setEncashEmployee] = useState(null);
  const [statutoryEmployee, setStatutoryEmployee] = useState(null);
  const [structureEmployee, setStructureEmployee] = useState(null);
  const [structureAutoOpenKey, setStructureAutoOpenKey] = useState(0);
  /** Actions (default) | Run Payroll — under Run Payroll sidebar item */
  const [runPayrollPanelView, setRunPayrollPanelView] = useState("actions");
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [activePayrollReportsSubTab, setActivePayrollReportsSubTab] = useState("salary-revision");
  const [payrollReportsSubTabTouched, setPayrollReportsSubTabTouched] = useState(false);
  const [payrollActionLog, setPayrollActionLog] = useState(() => {
    try {
      const raw = localStorage.getItem(PAYROLL_INSIGHTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [mountTrigger, setMountTrigger] = useState(0); // Trigger to ensure fetch on mount

  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [payrollSettingsTab, setPayrollSettingsTab] = useState("general"); // "general" | "investmentDeclaration"
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const canPayrollActionScreen = useCallback(
    (screenKey, action = "view") =>
      isAdmin || hasScreenAccess("payroll", screenKey, action),
    [isAdmin, hasScreenAccess]
  );
  const PAY_ACT_VIEW_KEYS = [
    "pay_act_assigned_structure",
    "pay_act_salary_revision",
    "pay_act_update_statutory",
    "pay_act_tax_planning",
    "pay_act_loan",
    "pay_act_leave_encashment",
    "pay_act_advance",
    "pay_act_reimbursement",
    "pay_act_process_fnf",
  ];
  const canAnyPayrollAction =
    isAdmin || PAY_ACT_VIEW_KEYS.some((k) => hasScreenAccess("payroll", k, "view"));
  const canRunPayrollWizard =
    isAdmin ||
    hasModuleAccess("payroll", "view") ||
    hasScreenAccess("payroll", "run_payroll", "view") ||
    hasScreenAccess("payroll", "process_payroll", "view");
  const canViewPayrollWorkspace =
    isAdmin ||
    hasModuleAccess("payroll", "view") ||
    hasScreenAccess("payroll", "payroll_dashboard", "view") ||
    hasScreenAccess("payroll", "assigned_structure", "view") ||
    hasScreenAccess("payroll", "run_payroll", "view") ||
    canAnyPayrollAction;
  // Dashboard is its own screen; other payroll access no longer opens it.
  const canViewPayrollDashboard =
    isAdmin || hasScreenAccess("payroll", "payroll_dashboard", "view");
  const canLoan =
    isAdmin ||
    canPayrollActionScreen("pay_act_loan", "view") ||
    hasScreenAccess("payroll", "loan", "view");
  const canLeaveEncashment =
    isAdmin ||
    canPayrollActionScreen("pay_act_leave_encashment", "view") ||
    hasScreenAccess("payroll", "leave_encashment", "view");
  const canViewPayrollReports =
    isAdmin ||
    hasScreenAccess("payroll", "salary_revision_report", "view") ||
    hasScreenAccess("payroll", "unpaid_salary_report", "view") ||
    hasScreenAccess("payroll", "form16_report", "view") ||
    hasScreenAccess("payroll", "payslips", "view") ||
    hasScreenAccess("payroll", "pay_proc_payslips", "view");
  const canExportSalaryRevisionReport =
    isAdmin ||
    hasScreenAccess("payroll", "salary_revision_report", "export");
  const canViewUnpaidSalaryReport =
    isAdmin ||
    hasScreenAccess("payroll", "unpaid_salary_report", "view");
  const canExportUnpaidSalaryReport =
    isAdmin ||
    hasScreenAccess("payroll", "unpaid_salary_report", "export");
  const canViewForm16Report =
    isAdmin || hasScreenAccess("payroll", "form16_report", "view");
  const canExportForm16Report =
    isAdmin || hasScreenAccess("payroll", "form16_report", "export");
  const canViewForm24Q =
    isAdmin ||
    hasScreenAccess("payroll", "form24q_filing", "view");
  const canViewStatutoryFiling =
    isAdmin ||
    hasModuleAccess("payroll", "view") ||
    hasScreenAccess("payroll", "statutory_filing", "view") ||
    hasScreenAccess("payroll", "form24q_filing", "view");
  const canExportPayroll =
    isAdmin ||
    hasModuleAccess("payroll", "export") ||
    hasScreenAccess("payroll", "payroll_dashboard", "export") ||
    hasScreenAccess("payroll", "assigned_structure", "export") ||
    hasScreenAccess("payroll", "pay_proc_payslips", "export") ||
    hasScreenAccess("payroll", "payslips", "export");
  const canImportPayroll =
    isAdmin ||
    hasModuleAccess("payroll", "create") ||
    hasScreenAccess("payroll", "process_payroll", "create") ||
    hasScreenAccess("payroll", "run_payroll", "create");
  const canApprovePayroll =
    isAdmin ||
    canApproveProcessStep(hasScreenAccess, "payroll", "finalise", PAYROLL_STEP_RBAC) ||
    hasModuleAccess("payroll", "approve") ||
    hasScreenAccess("payroll", "process_payroll", "approve") ||
    hasScreenAccess("payroll", "run_payroll", "approve");
  const payrollSettingsScreens = [
    "payroll_settings",
    "salary_structure",
    "statutory_rates",
    "formula_manager",
    "tds_reference_heads",
    "tds_engine_compliance",
    "loan_policies",
    "leave_encashment_policies",
  ];
  const canOpenPayrollSettings =
    isAdmin ||
    hasModuleAccess("payroll", "view") ||
    hasModuleAccess("payroll", "edit") ||
    payrollSettingsScreens.some(
      (screen) =>
        hasScreenAccess("payroll", screen, "view") ||
        hasScreenAccess("payroll", screen, "edit") ||
        hasScreenAccess("payroll", screen, "create")
    );
  const canEditPayrollSettings =
    isAdmin ||
    hasModuleAccess("payroll", "edit") ||
    hasScreenAccess("payroll", "payroll_settings", "edit") ||
    hasScreenAccess("payroll", "salary_structure", "edit") ||
    hasScreenAccess("payroll", "statutory_rates", "edit") ||
    hasScreenAccess("payroll", "formula_manager", "edit") ||
    hasScreenAccess("payroll", "tds_reference_heads", "edit") ||
    hasScreenAccess("payroll", "tds_engine_compliance", "edit") ||
    hasScreenAccess("payroll", "loan_policies", "edit") ||
    hasScreenAccess("payroll", "leave_encashment_policies", "edit");
  const canViewChecklistTasks =
    isAdmin ||
    hasModuleAccess("payroll", "view") ||
    hasScreenAccess("payroll", "payroll_checklist", "view") ||
    hasScreenAccess("payroll", "run_payroll", "view");
  const canEditChecklistTasks =
    isAdmin ||
    hasModuleAccess("payroll", "edit") ||
    hasScreenAccess("payroll", "payroll_checklist", "create") ||
    hasScreenAccess("payroll", "payroll_checklist", "edit");
  const canReviseSalary =
    isAdmin ||
    hasModuleAccess("payroll", "edit") ||
    hasScreenAccess("payroll", "assigned_structure", "edit") ||
    hasScreenAccess("payroll", "salary_revision_import", "create") ||
    hasScreenAccess("payroll", "salary_revision_import", "import");
  const canImportStructureComponents =
    isAdmin ||
    hasModuleAccess("payroll", "edit") ||
    hasScreenAccess("payroll", "salary_structure_component_import", "view") ||
    hasScreenAccess("payroll", "salary_structure_component_import", "create") ||
    hasScreenAccess("payroll", "salary_structure_component_import", "import");
  const canUpdateStatutory =
    isAdmin ||
    hasModuleAccess("payroll", "edit") ||
    hasScreenAccess("payroll", "update_statutory_applicability", "edit") ||
    hasScreenAccess("payroll", "assigned_structure", "edit");
  const canViewAnyPayrollTab = canViewPayrollWorkspace || canViewPayrollReports;
  // Landing tab: Dashboard when granted, otherwise the first tab the role can use.
  const payrollHomeTab = canViewPayrollDashboard
    ? "dashboard"
    : canViewPayrollWorkspace
      ? "run_payroll"
      : canViewPayrollReports
        ? "reports"
        : "settings";

  useEffect(() => {
    if (!canViewAnyPayrollTab) return;
    if (activePayrollTab === "dashboard" && !canViewPayrollDashboard) {
      setActivePayrollTab(payrollHomeTab);
    } else if (
      ["payroll", "assigned_structure", "dashboard", "run_payroll"].includes(activePayrollTab) &&
      !canViewPayrollWorkspace
    ) {
      setActivePayrollTab("reports");
    } else if (activePayrollTab === "reports" && !canViewPayrollReports) {
      setActivePayrollTab(payrollHomeTab);
    } else if (activePayrollTab === "statutory_filing" && !canViewStatutoryFiling) {
      setActivePayrollTab(payrollHomeTab);
    } else if (activePayrollTab === "settings" && !canOpenPayrollSettings) {
      setActivePayrollTab(payrollHomeTab);
    } else if (activePayrollTab === "payroll" || activePayrollTab === "assigned_structure") {
      setActivePayrollTab("run_payroll");
      setRunPayrollPanelView("actions");
    }
  }, [
    activePayrollTab,
    canViewAnyPayrollTab,
    canViewPayrollReports,
    canViewPayrollWorkspace,
    canViewPayrollDashboard,
    payrollHomeTab,
    canViewStatutoryFiling,
    canOpenPayrollSettings,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(PAYROLL_INSIGHTS_KEY, JSON.stringify(payrollActionLog));
    } catch {
      /* ignore */
    }
  }, [payrollActionLog]);

  const addPayrollInsight = useCallback(
    ({ action, employeeId, status, pendingAt, approvedBy }) => {
      const emp = (allEmployees || []).find((e) => String(e.id) === String(employeeId));
      const employeeCode =
        emp?.empCode || emp?.emp_code || emp?.employeeCode || emp?.employee_code || "-";
      const when = new Date().toLocaleString();
      setPayrollActionLog((prev) => [
        {
          id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
          action,
          employeeCode,
          pendingAt: pendingAt || "Payroll Team",
          status: status || "Pending",
          approvedBy: approvedBy || "-",
          when,
        },
        ...prev,
      ]);
    },
    [allEmployees]
  );

  const payrollInsightItems = useMemo(
    () =>
      payrollActionLog.length
        ? payrollActionLog
        : [
            {
              action: "Payroll Lock",
              employeeCode: "-",
              pendingAt: "Finance Head",
              status: "Pending",
              approvedBy: "-",
              when: "-",
            },
          ],
    [payrollActionLog]
  );
  const [incomeTaxGuidePreview, setIncomeTaxGuidePreview] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: "",
  });
  const incomeTaxGuideBlobUrlRef = useRef(null);
  const incomeTaxGuideDownloadUrl = useMemo(
    () =>
      `${String(API_URL || "").replace(/\/+$/, "")}/income-tax-guide/download`,
    []
  );
  const openIncomeTaxGuidePreview = useCallback(async () => {
    try {
      const response = await api.get("/income-tax-guide", {
        responseType: "blob",
      });
      const blob = response?.data;
      if (!(blob instanceof Blob)) {
        toast.error("Unable to load Income Tax Guide.");
        return;
      }
      if (incomeTaxGuideBlobUrlRef.current) {
        URL.revokeObjectURL(incomeTaxGuideBlobUrlRef.current);
      }
      const blobUrl = URL.createObjectURL(blob);
      incomeTaxGuideBlobUrlRef.current = blobUrl;
      setIncomeTaxGuidePreview({
        isOpen: true,
        url: blobUrl,
        downloadUrl: incomeTaxGuideDownloadUrl,
        fileName: "INCOME TAX GUIDE 2026-27.pdf",
      });
    } catch (error) {
      console.error("Failed to load income tax guide preview:", error);
      toast.error("Unable to open Income Tax Guide.");
    }
  }, [incomeTaxGuideDownloadUrl]);

  const getCompanyCodeForExcel = useCallback(() => {
    try {
      const raw = localStorage.getItem("companyData");
      if (!raw) return "COMPANY";
      const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
      const code = String(companyData?.code || "")
        .trim()
        .replace(/[^A-Za-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();
      return code || "COMPANY";
    } catch {
      return "COMPANY";
    }
  }, []);

  const buildPayrollExcelFilename = useCallback((name) => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    const safeName = String(name || "Export")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
    return `${getCompanyCodeForExcel()}_${safeName}_${mm}_${yyyy}.xlsx`;
  }, [getCompanyCodeForExcel]);
  // Separate API params (for backend queries) from form state (for modal persistence)
  const [appliedFilters, setAppliedFilters] = useState({});
  const [appliedFiltersForm, setAppliedFiltersForm] = useState({});

  // Investment declaration specific filters
  const [selectedTaxRegimes, setSelectedTaxRegimes] = useState([]); // ['OLD', 'NEW']
  const [selectedDeclarationStatuses, setSelectedDeclarationStatuses] = useState([]); // ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', etc.]
  const [taxRegimeDropdownOpen, setTaxRegimeDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Check if any non-default filters are currently active
  // Includes: search, advanced Filters modal, tax regime, declaration status, company selection.
  const hasActiveFilters = useMemo(() => {
    const hasSearch = Boolean(
      typeof debouncedSearchTerm === "string"
        ? debouncedSearchTerm.trim()
        : debouncedSearchTerm
    );

    const hasCompanyFilter =
      typeof selectedCompany === "string" && selectedCompany !== "all";

    const hasInvestmentFilters =
      (selectedTaxRegimes || []).length > 0 ||
      (selectedDeclarationStatuses || []).length > 0;

    const hasAdvancedFilters = Object.entries(appliedFilters || {}).some(([k, v]) => {
      if (k === "dateFilterType") return Boolean(v && v !== "all");
      if (v === undefined || v === null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") return v.trim() !== "";
      return true;
    });

    return hasSearch || hasCompanyFilter || hasInvestmentFilters || hasAdvancedFilters;
  }, [
    debouncedSearchTerm,
    selectedCompany,
    selectedTaxRegimes,
    selectedDeclarationStatuses,
    appliedFilters,
  ]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setTaxRegimeDropdownOpen(false);
        setStatusDropdownOpen(false);
      }
    };

    if (taxRegimeDropdownOpen || statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [taxRegimeDropdownOpen, statusDropdownOpen]);

  const clearFilters = () => {
    // Clear both API params and form state
    setAppliedFilters({});
    setAppliedFiltersForm({});
    // Clear investment declaration filters
    setSelectedTaxRegimes([]);
    setSelectedDeclarationStatuses([]);
  };

  // Template download modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  // Download confirmation modal state
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);

  // Investment declaration settings (UI lives in InvestmentDeclarationSettings)
  const investmentSettingsRef = useRef(null);
  const [investmentFlags, setInvestmentFlags] = useState({
    enableInvestmentDeclaration: false,
    enableInvestmentDeclarationWithProof: false,
    enableTaxRegimeLock: true,
  });
  const {
    enableInvestmentDeclaration,
    enableInvestmentDeclarationWithProof,
    enableTaxRegimeLock,
  } = investmentFlags;
  const handleInvestmentFlagsChange = useCallback((flags) => {
    setInvestmentFlags((prev) => ({ ...prev, ...flags }));
  }, []);

  // Bulk investment declaration headers (tax regime + status) per employee
  const [investmentHeaders, setInvestmentHeaders] = useState({}); // key: employeeId -> header
  const isMountedRef = useRef(false);
  const settingsLoadInFlightRef = useRef(null);

  // Apply loaded settings to state
  const applyPayrollSettingsToState = (settings) => {
    if (!settings) return;
    setPayrollSettings(settings);
    investmentSettingsRef.current?.loadFromSettings?.(settings);
    setInvestmentFlags({
      enableInvestmentDeclaration: !!settings.isInvestmentEnabled,
      enableInvestmentDeclarationWithProof: !!settings.isProofEnabled,
      enableTaxRegimeLock: settings.isTaxRegimeLocked !== false,
    });
  };

  // Function to load payroll settings from backend
  const loadPayrollSettings = async () => {
    if (settingsLoadInFlightRef.current) {
      return settingsLoadInFlightRef.current;
    }

    const loadPromise = (async () => {
      setLoadingSettings(true);
      try {
        const response = await payrollService.getPayrollSettings();

        if (response.success && response.data) {
          applyPayrollSettingsToState(response.data);
        } else if (response.success === true && response.data === null) {
          // First-time company: keep form defaults; create happens on explicit Save
          setPayrollSettings(null);
        } else {
          setPayrollSettings(null);
        }
      } catch (error) {
        setPayrollSettings(null);
        if (error?.response?.status !== 404) {
          console.error("Error loading payroll settings:", error);
        }
      } finally {
        setLoadingSettings(false);
        settingsLoadInFlightRef.current = null;
      }
    })();

    settingsLoadInFlightRef.current = loadPromise;
    return loadPromise;
  };

  // Helper function to get name by ID from list
  const getNameByIdFromList = (id, list) => {
    if (!id || !list) return "N/A";
    const item = list.find((item) => item.id === id);
    return item ? item.name : "N/A";
  };

  // Helper function to get month name by number
  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[parseInt(monthNumber) - 1] || "January";
  };

  // Helper function to generate unified template data with both tax regimes
  const generateUnifiedTemplateData = () => {
    return {
      "Sl No": 1,
      "Employee Code": "EMP001",
      "Employee Name": "John Doe",
      "Tax Regime": "Old", // Default to Old, user can change

      // HRA Section - Old Regime Columns
      "HRA - City Type (Old)": "",
      "HRA - Rent Paid (Old)": "",
      "HRA - Landlord PAN (Old)": "",
      "HRA - Address (Old)": "",

      // Section 80C - Old Regime Columns
      "80C - Life Insurance Premium (Old)": "",
      "80C - PPF Contribution (Old)": "",
      "80C - VPF Contribution (Old)": "",
      "80C - Pension Fund (Old)": "",
      "80C - NSC Investment (Old)": "",
      "80C - NSC Interest (Old)": "",
      "80C - ULIP Investment (Old)": "",
      "80C - ELSS Investment (Old)": "",
      "80C - Tuition Fees (Old)": "",
      "80C - Principal Repayment (Old)": "",
      "80C - Registration Charges (Old)": "",
      "80C - Sukanya Samriddhi (Old)": "",
      "80C - Infrastructure Bonds (Old)": "",
      "80C - Fixed Deposit (Old)": "",

      // VIA Deductions - Old Regime Columns
      "VIA - Self Medical Insurance 80D (Old)": "",
      "VIA - Parents Medical Insurance 80D (Old)": "",
      "VIA - Senior Citizen Parents 80D (Old)": "",
      "VIA - Preventive Health Checkup (Old)": "",
      "VIA - Education Loan Interest 80E (Old)": "",
      "VIA - NPS Additional 80CCD1B (Old)": "",
      "VIA - Home Loan Interest 80EE (Old)": "",
      "VIA - Home Loan Interest 80EEA (Old)": "",
      "VIA - Home Loan Interest 80EEB (Old)": "",
      "VIA - Donations 80G (Old)": "",
      "VIA - Savings Interest 80TTA (Old)": "",
      "VIA - Deposit Interest 80TTB (Old)": "",

      // House Property - Old Regime Columns
      "House Property - Property Type (Old)": "",
      "House Property - Loan Before April 1999 (Old)": "",
      "House Property - Interest Paid (Old)": "",
      "House Property - Rental Income (Old)": "",
      "House Property - Address (Old)": "",

      // New Regime Columns (All Empty - No Deductions Applicable)
      "HRA - City Type (New)": "",
      "HRA - Rent Paid (New)": "",
      "HRA - Landlord PAN (New)": "",
      "HRA - Address (New)": "",

      "80C - Life Insurance Premium (New)": "",
      "80C - PPF Contribution (New)": "",
      "80C - VPF Contribution (New)": "",
      "80C - Pension Fund (New)": "",
      "80C - NSC Investment (New)": "",
      "80C - NSC Interest (New)": "",
      "80C - ULIP Investment (New)": "",
      "80C - ELSS Investment (New)": "",
      "80C - Tuition Fees (New)": "",
      "80C - Principal Repayment (New)": "",
      "80C - Registration Charges (New)": "",
      "80C - Sukanya Samriddhi (New)": "",
      "80C - Infrastructure Bonds (New)": "",
      "80C - Fixed Deposit (New)": "",

      "VIA - Self Medical Insurance 80D (New)": "",
      "VIA - Parents Medical Insurance 80D (New)": "",
      "VIA - Senior Citizen Parents 80D (New)": "",
      "VIA - Preventive Health Checkup (New)": "",
      "VIA - Education Loan Interest 80E (New)": "",
      "VIA - NPS Additional 80CCD1B (New)": "",
      "VIA - Home Loan Interest 80EE (New)": "",
      "VIA - Home Loan Interest 80EEA (New)": "",
      "VIA - Home Loan Interest 80EEB (New)": "",
      "VIA - Donations 80G (New)": "",
      "VIA - Savings Interest 80TTA (New)": "",
      "VIA - Deposit Interest 80TTB (New)": "",

      "House Property - Property Type (New)": "",
      "House Property - Loan Before April 1999 (New)": "",
      "House Property - Interest Paid (New)": "",
      "House Property - Rental Income (New)": "",
      "House Property - Address (New)": "",

      // Additional fields
      "Total Deductions": "",
      "Declaration Status": "Not Submitted",
      "Last Updated": "",
      Remarks:
        "Fill Old Regime columns if Tax Regime = Old, leave New Regime columns empty. Fill New Regime columns if Tax Regime = New, leave Old Regime columns empty.",
    };
  };

  // Helper function to get financial year end date
  const getFinancialYearEndDate = (fy) => {
    const year = parseInt(fy.split("-")[1]);
    return new Date(year, 2, 31); // March 31st of the end year
  };

  // Helper function to generate financial years
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-12

    const years = [];

    // If current month is April or later, current FY is currentYear-currentYear+1
    // If current month is before April, current FY is currentYear-1-currentYear
    const currentFY =
      currentMonth >= 4
        ? `${currentYear}-${currentYear + 1}`
        : `${currentYear - 1}-${currentYear}`;

    // Generate years from 5 years ago to current year only (no future years)
    for (let i = -5; i <= 0; i++) {
      const fyYear = currentMonth >= 4 ? currentYear + i : currentYear - 1 + i;
      const fy = `${fyYear}-${fyYear + 1}`;
      years.push(fy);
    }

    return years.sort().reverse(); // Most recent first
  };

  const monthRangeValue = useMemo(
    () => getMonthRangeValue(selectedYear, selectedMonth),
    [selectedMonth, selectedYear]
  );

  useEffect(() => {
    setMonthPickerValue(monthRangeValue);
  }, [monthRangeValue]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("companyData");
      if (!raw) return;
      const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
      const commencesOn = companyData?.serviceCommencesOn;
      if (!commencesOn) return;
      const date = new Date(commencesOn);
      if (Number.isNaN(date.getTime())) return;
      setMinDate(date);
    } catch (e) {
      console.warn("Payroll minDate from companyData.serviceCommencesOn:", e);
    }
  }, []);

  useEffect(() => {
    if (!minDate) return;
    const minYear = minDate.getFullYear();
    const minMonth = minDate.getMonth();
    if (
      selectedYear < minYear ||
      (selectedYear === minYear && selectedMonth < minMonth)
    ) {
      setSelectedYear(minYear);
      setSelectedMonth(minMonth);
      setMonthPickerValue(getMonthRangeValue(minYear, minMonth));
    }
  }, [minDate, selectedMonth, selectedYear]);

  const handleMonthRangeChange = (eventOrValue) => {
    const rawValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value || "";

    const applyMonthChange = (nextYear, nextMonth) => {
      setSelectedYear(nextYear);
      setSelectedMonth(nextMonth);
      setMonthPickerValue(getMonthRangeValue(nextYear, nextMonth));
      setPage(1);
    };

    if (!rawValue) {
      const now = new Date();
      if (now.getFullYear() === selectedYear && now.getMonth() === selectedMonth) {
        return;
      }
      applyMonthChange(now.getFullYear(), now.getMonth());
      return;
    }

    const [start] = rawValue.split("/");
    if (!start) return;

    const [yearString, monthString] = start.split("-");
    const parsedYear = parseInt(yearString, 10);
    const parsedMonth = parseInt(monthString, 10) - 1;

    if (Number.isNaN(parsedYear) || Number.isNaN(parsedMonth)) return;
    if (parsedYear === selectedYear && parsedMonth === selectedMonth) return;

    applyMonthChange(parsedYear, parsedMonth);
  };

  // Helper function to get employee relieving date (display) — same priority as scope filter
  const getEmployeeRelievingDate = (employee) => {
    if (!employee) return null;
    return (
      employee.relievedDate ||
      employee.lastWorkingDay ||
      employee.leaveDate ||
      employee.dateOfLeaving ||
      employee.terminationDate ||
      employee.resignationDate ||
      employee.exitDate ||
      employee.endDate ||
      employee.separationDate ||
      null
    );
  };

  // Filter employees based on financial year and employment status
  const filterEmployeesByFinancialYear = (employees, fy) => {
    const fyEndDate = getFinancialYearEndDate(fy);
    const fyStartDate = new Date(fyEndDate.getFullYear() - 1, 3, 1); // April 1st of start year

    return employees.filter((employee) => {
      const relievingDate = getEmployeeRelievingDate(employee);
      const joiningDate = new Date(
        employee.dateOfJoin ||
          employee.joiningDate ||
          employee.joinDate ||
          "1900-01-01"
      );

      // Basic validation
      if (Number.isNaN(joiningDate.getTime())) {
        return false;
      }

      // Must have joined on or before FY end
      if (joiningDate > fyEndDate) {
        return false;
      }

      // If there is a relieving/exit date and it is before FY start,
      // the employee was not active at any point in this FY
      if (relievingDate) {
        const empRelievingDate = new Date(relievingDate);
        if (!Number.isNaN(empRelievingDate.getTime()) && empRelievingDate < fyStartDate) {
          return false;
        }
      }

      // Otherwise, the employee overlaps with the FY window
      return true;
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearchTerm(value);
      }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  // Fetch master data on mount
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [
          departmentsResponse,
          designationsResponse,
          branchesResponse,
          gradesResponse,
          categoriesResponse,
          subDepartmentsResponse,
        ] = await Promise.all([
          departmentService.getDepartments(),
          designationService.getDesignations(),
          branchService.getBranches(),
          gradesService.getGrades(),
          categoryService.getAllCategories(),
          subDepartmentService.getAllSubDepartments(),
        ]);

        if (departmentsResponse.data) {
          setDepartmentsList(departmentsResponse.data || []);
        } else if (
          departmentsResponse.success &&
          departmentsResponse.departments
        ) {
          setDepartmentsList(departmentsResponse.departments || []);
        } else {
          setDepartmentsList([]);
        }

        if (designationsResponse.data) {
          setDesignationsList(designationsResponse.data || []);
        } else if (
          designationsResponse.success &&
          designationsResponse.designations
        ) {
          setDesignationsList(designationsResponse.designations || []);
        } else {
          setDesignationsList([]);
        }
        // branches
        if (branchesResponse?.data)
          setBranchesList(branchesResponse.data || []);
        else if (branchesResponse?.success && branchesResponse?.branches)
          setBranchesList(branchesResponse.branches || []);
        else setBranchesList([]);

        // grades
        if (gradesResponse?.data) setGradesList(gradesResponse.data || []);
        else if (gradesResponse?.success && gradesResponse?.grades)
          setGradesList(gradesResponse.grades || []);
        else setGradesList([]);

        // categories
        if (categoriesResponse?.data)
          setCategoriesList(categoriesResponse.data || []);
        else if (categoriesResponse?.success && categoriesResponse?.categories)
          setCategoriesList(categoriesResponse.categories || []);
        else setCategoriesList([]);

        // sub departments
        if (subDepartmentsResponse?.data)
          setSubDepartmentsList(subDepartmentsResponse.data || []);
        else if (
          subDepartmentsResponse?.success &&
          subDepartmentsResponse?.subDepartments
        )
          setSubDepartmentsList(subDepartmentsResponse.subDepartments || []);
        else setSubDepartmentsList([]);
      } catch (error) {
        console.error("Error fetching master data:", error);
      }
    };

    fetchMasterData();
  }, []);

  // Load salary structures + statutory config (same source as employee profile breakdown)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tplRes, statRes] = await Promise.all([
          salaryStructureService.listTemplates(),
          salaryStructureService.getStatutoryConfig(),
        ]);
        if (cancelled) return;
        const structures = templatesToStructures(tplRes?.data);
        setSalaryStructures(structures);
        if (statRes?.data?.configJson) {
          setStatutoryConfigJson(statRes.data.configJson);
        }
        const method =
          getAuthJson("companyData")?.defaultSalaryStructureMethod || "CTC";
        setOrgSalaryMethod(method);
      } catch (err) {
        console.error("Failed to load salary structures for payroll grid", err);
        if (!cancelled) {
          setSalaryStructures([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch employees without pagination - fetch all employees
  useEffect(() => {
    const fetchEmployees = async () => {
      // Ensure component is initialized before fetching
      if (mountTrigger === 0 && !isMountedRef.current) {
        return;
      }
      
      setIsLoading(true);
      setFetchError(null);
      try {
        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December",
        ];
        const params = {
          page: 1,
          limit: itemsPerPage === "all" ? 10000 : 10000, // Fetch all employees
          search: debouncedSearchTerm,
          sortBy: sortField,
          sortOrder: sortDirection,
          financialYear: selectedFinancialYear,
          // Server-side: drop employees whose relievedDate is before this pay month
          payslipMonthYear: `${monthNames[selectedMonth]}-${selectedYear}`,
          excludeEmploymentTypes: "External,External", // Exclude external from payroll listing
          ...appliedFilters,
        };

        // Fetch employees and investment headers in parallel
        const [employeesResponse, headersResponse] = await Promise.allSettled([
          employeeService.getEmployees(params),
          selectedFinancialYear
            ? payrollService.getInvestmentHeaders(selectedFinancialYear)
            : Promise.resolve({ success: false })
        ]);

        // Handle employees response
        if (employeesResponse.status === 'fulfilled' && employeesResponse.value?.success) {
          const data = employeesResponse.value.data || [];
          setAllEmployees(data);
          setTotalEmployees(
            employeesResponse.value.pagination?.totalItems || employeesResponse.value.total || data.length || 0
          );
          setTotalPages(1);
          setFetchError(null);
        } else if (employeesResponse.status === 'rejected') {
          const errorMsg = employeesResponse.reason?.message || employeesResponse.reason?.response?.data?.message || "Failed to fetch employees";
          setFetchError(errorMsg);
          setAllEmployees([]);
          toast.error(errorMsg);
        } else if (employeesResponse.status === 'fulfilled' && !employeesResponse.value?.success) {
          const errorMsg = employeesResponse.value?.message || "Failed to fetch employees";
          setFetchError(errorMsg);
          setAllEmployees([]);
          toast.error(errorMsg);
        } else {
          setAllEmployees([]);
          setFetchError(null);
        }

        // Handle investment headers response
        if (headersResponse.status === 'fulfilled' && headersResponse.value?.success && Array.isArray(headersResponse.value.data)) {
          const map = {};
          headersResponse.value.data.forEach((header) => {
            if (header && header.employeeId != null) {
              map[header.employeeId] = header;
            }
          });
          setInvestmentHeaders(map);
        } else if (headersResponse.status === 'rejected') {
          console.error("Error loading investment headers:", headersResponse.reason);
          // Don't show toast for headers error, just log it
        } else if (!selectedFinancialYear) {
          setInvestmentHeaders({});
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        const errorMsg = error?.message || error?.response?.data?.message || "An unexpected error occurred";
        setFetchError(errorMsg);
        setAllEmployees([]);
        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
        isMountedRef.current = true;
      }
    };

    // Always fetch on mount or when dependencies change
    fetchEmployees();
  }, [
    itemsPerPage,
    debouncedSearchTerm,
    selectedFinancialYear,
    selectedMonth,
    selectedYear,
    appliedFilters,
    sortField,
    sortDirection,
    mountTrigger, // Include mount trigger to ensure fetch on component mount
  ]);

  // Initialize component and ensure data loads on mount (when navigating to Payroll page)
  useEffect(() => {
    // Reset state and trigger fetch on every mount/access
    setIsLoading(true);
    setFetchError(null);
    setAllEmployees([]);
    setInvestmentHeaders({});
    isMountedRef.current = false;
    
    // Trigger initial fetch
    setMountTrigger(prev => prev + 1);
    isMountedRef.current = true;
  }, []); // Run only on mount

  // Ensure data loads whenever Payroll route is accessed (navigation, refresh, etc.)
  useEffect(() => {
    // When location pathname includes 'payroll', ensure we're on the Payroll page
    if (location.pathname.includes('/payroll')) {
      // Only trigger fetch if we haven't attempted to load yet (isMountedRef is false)
      // Don't check allEmployees.length as it can be 0 legitimately (no results from search)
      if (!isMountedRef.current && !isLoading) {
        const timer = setTimeout(() => {
          setMountTrigger(prev => prev + 1);
        }, 100);
        return () => clearTimeout(timer);
      }
      // Also ensure we're on the payroll tab
      // if (activePayrollTab !== "payroll") {
      //   setActivePayrollTab("payroll");
      // }
    }
  }, [location.pathname, isLoading, activePayrollTab]);

  // Refetch data when Payroll tab is activated (in case component is reused)
  useEffect(() => {
    if (activePayrollTab === "run_payroll" || activePayrollTab === "dashboard") {
      if (!isMountedRef.current && !isLoading) {
        const timer = setTimeout(() => {
          setMountTrigger(prev => prev + 1);
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [activePayrollTab, isLoading]);

  // Update filtered employees - Direct pass-through as backend handles filtering
  useEffect(() => {
    setEmployees(flattenEmployeeList(allEmployees || []));
  }, [allEmployees]);

  // Load payroll settings when Payroll is opened: from sidebar (pathname includes payroll) or when Payroll tab is clicked
  useEffect(() => {
    if (location.pathname.includes("/payroll")) loadPayrollSettings();
  }, [location.pathname]);
  useEffect(() => {
    if (activePayrollTab === "run_payroll") loadPayrollSettings();
  }, [activePayrollTab]);

  // Tax regime lock is enforced inside InvestmentDeclarationSettings when both are OFF.

  // Load investment declaration headers for the selected financial year
  // This ensures headers are always available, even if fetchEmployees hasn't completed yet
  const loadInvestmentHeaders = useCallback(async () => {
    try {
      if (!selectedFinancialYear) {
        setInvestmentHeaders({});
        return;
      }

      const response = await payrollService.getInvestmentHeaders(
        selectedFinancialYear
      );

      if (response.success && Array.isArray(response.data)) {
        const map = {};
        response.data.forEach((header) => {
          if (header && header.employeeId != null) {
            map[header.employeeId] = header;
          }
        });
        setInvestmentHeaders(map);
      } else {
        setInvestmentHeaders({});
      }
    } catch (error) {
      console.error("Error loading investment headers for payroll list:", error);
      // Don't show toast for headers error, just log it
      setInvestmentHeaders({});
    }
  }, [selectedFinancialYear]);

  useEffect(() => {
    loadInvestmentHeaders();
  }, [loadInvestmentHeaders]);

  // Helper functions for business logic based on payroll settings
  const isInvestmentDeclarationEnabled = () => {
    return (
      payrollSettings?.isInvestmentEnabled || enableInvestmentDeclaration
    );
  };

  const isInvestmentProofEnabled = () => {
    return (
      payrollSettings?.isProofEnabled || enableInvestmentDeclarationWithProof
    );
  };

  const isTaxRegimeLocked = () => {
    return payrollSettings?.isTaxRegimeLocked || enableTaxRegimeLock;
  };

  const getInvestmentDeclarationMode = () => {
    if (payrollSettings?.investmentWindows) {
      return "always";
    }
    const mode = payrollSettings?.investmentMode || "ALWAYS";
    switch (String(mode).toUpperCase()) {
      case "AUTO_MONTHLY":
        return "auto";
      case "ALWAYS":
        return "always";
      case "MANUAL":
      default:
        return "manual";
    }
  };

  const getInvestmentProofMode = () => {
    return String(payrollSettings?.proofMode || "manual").toLowerCase();
  };

  // Example usage of payroll settings for business logic:
  // - Use isInvestmentDeclarationEnabled() to show/hide investment declaration features
  // - Use isInvestmentProofEnabled() to show/hide proof upload features  
  // - Use isTaxRegimeLocked() to prevent tax regime changes
  // - Use getInvestmentDeclarationMode() to determine if investment is manual/auto/always
  // - Use getInvestmentProofMode() to determine if proof upload is manual/auto/always

  // Apply client-side financial year filtering for Payroll view
  const filteredEmployeesByMonth = filterEmployeesByCalendarMonth(
    employees,
    selectedYear,
    selectedMonth
  );

  // Apply tax regime and declaration status filters
  const filteredEmployees = useMemo(() => {
    let filtered = filteredEmployeesByMonth;

    // Filter by tax regime
    if (selectedTaxRegimes.length > 0) {
      filtered = filtered.filter((emp) => {
        const header = investmentHeaders[emp.id];
        const regimeRaw = header?.taxRegime || emp.taxRegime || "NEW";
        const regime = regimeRaw.toString().toUpperCase();
        return selectedTaxRegimes.includes(regime);
      });
    }

    // Filter by declaration status
    if (selectedDeclarationStatuses.length > 0) {
      filtered = filtered.filter((emp) => {
        const header = investmentHeaders[emp.id];
        const rawStatus = header?.status;
        
        // Check if filtering for DEFAULT (no status/null/undefined)
        if (selectedDeclarationStatuses.includes("DEFAULT")) {
          // If DEFAULT is selected, include employees with no status
          if (!rawStatus || rawStatus === null || rawStatus === undefined) {
            return true;
          }
        }
        
        // If no status and DEFAULT is not selected, exclude
        if (!rawStatus || rawStatus === null || rawStatus === undefined) {
          return false;
        }
        
        const status = rawStatus.toString().toUpperCase();
        return selectedDeclarationStatuses.includes(status);
      });
    }

    return filtered;
  }, [
    filteredEmployeesByMonth,
    selectedTaxRegimes,
    selectedDeclarationStatuses,
    investmentHeaders,
  ]);

  // Currently sorting is done on backend; keep order but operate on filtered list
  const sortedEmployees = filteredEmployees;

  // Limit employees to the selected count (10, 25, 50, 100, or all)
  const displayEmployees = useMemo(() => {
    if (itemsPerPage === "all") {
      return sortedEmployees;
    }
    const limit = parseInt(itemsPerPage) || 10;
    return sortedEmployees.slice(0, limit);
  }, [sortedEmployees, itemsPerPage]);

  // Same breakdown engine as Employee Profile eye-modal (read-only for grid)
  const structuresForColumnFilter = useMemo(() => {
    if (!selectedStructureFilterIds.length) return salaryStructures;
    const idSet = new Set(selectedStructureFilterIds.map(String));
    return salaryStructures.filter((s) => idSet.has(String(s.id)));
  }, [salaryStructures, selectedStructureFilterIds]);

  // Load effective-dated statutory flags for the selected payroll month
  const filteredEmployeeIdsKey = useMemo(
    () =>
      (filteredEmployees || [])
        .map((e) => e.id)
        .filter(Boolean)
        .join(","),
    [filteredEmployees]
  );

  useEffect(() => {
    const ids = filteredEmployeeIdsKey
      ? filteredEmployeeIdsKey.split(",").map((id) => Number(id))
      : [];
    if (!ids.length) {
      setStatutoryOverridesByEmployeeId(new Map());
      return undefined;
    }
    const asOf = `${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}`;
    let cancelled = false;
    payrollOpsService
      .resolveStatutoryBatch(ids, asOf)
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data || {};
        const map = new Map();
        Object.entries(raw).forEach(([k, v]) => {
          map.set(Number(k), v);
          map.set(String(k), v);
        });
        setStatutoryOverridesByEmployeeId(map);
      })
      .catch(() => {
        if (!cancelled) setStatutoryOverridesByEmployeeId(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [filteredEmployeeIdsKey, selectedYear, selectedMonth]);

  const payrollBreakdownByEmployee = useMemo(() => {
    if (!salaryStructures.length || !filteredEmployees.length) {
      return new Map();
    }
    return enrichEmployeesWithSalaryBreakdown({
      employees: filteredEmployees,
      structures: salaryStructures,
      statutoryConfigJson,
      branchesList,
      selectedYear,
      selectedMonth,
      orgSalaryMethod,
      statutoryOverridesByEmployeeId,
    });
  }, [
    filteredEmployees,
    salaryStructures,
    statutoryConfigJson,
    branchesList,
    selectedYear,
    selectedMonth,
    orgSalaryMethod,
    statutoryOverridesByEmployeeId,
  ]);

  const payrollEligibleEmployees = useMemo(() => {
    if (!selectedStructureFilterIds.length) return filteredEmployees;
    return filteredEmployees.filter((e) => {
      const row = payrollBreakdownByEmployee.get(e.id);
      return row && selectedStructureFilterIds.map(String).includes(String(row.structureId));
    });
  }, [filteredEmployees, selectedStructureFilterIds, payrollBreakdownByEmployee]);

  /** Paginated rows for Assigned Structure grid only — not payroll validation scope */
  const assignedStructureEmployees = useMemo(() => {
    if (itemsPerPage === "all") return payrollEligibleEmployees;
    const limit = parseInt(itemsPerPage, 10) || 10;
    return payrollEligibleEmployees.slice(0, limit);
  }, [payrollEligibleEmployees, itemsPerPage]);

  const payrollBreakdownColumns = useMemo(
    () => buildPayrollBreakdownColumnKeys(structuresForColumnFilter),
    [structuresForColumnFilter]
  );

  const payrollComponentColumns = useMemo(() => {
    // Prefer template-derived order; merge any names seen on enriched rows
    const seen = new Set();
    const names = [];
    const push = (n) => {
      const name = String(n || "").trim();
      if (!name) return;
      const k = name.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      names.push(name);
    };
    (payrollBreakdownColumns || []).forEach(push);
    const sourceEmployees = selectedStructureFilterIds.length
      ? assignedStructureEmployees
      : filteredEmployees;
      console.log("sourceEmployees", sourceEmployees);
    sourceEmployees.forEach((emp) => {
      const row = payrollBreakdownByEmployee.get(emp.id);
      if (!row) return;
      (row.componentNames || []).forEach(push);
      Object.keys(row.amountsByName || {}).forEach(push);
    });
    [
      "PF (Employer)",
      "ESI (Employer)",
      "Gratuity",
      "PF (Employee)",
      "ESI (Employee)",
      "Professional Tax (PT)",
    ].forEach(push);
    return names;
  }, [
    payrollBreakdownColumns,
    payrollBreakdownByEmployee,
    assignedStructureEmployees,
    filteredEmployees,
    selectedStructureFilterIds,
  ]);

  const payrollScopeContext = useMemo(
    () => ({
      payMonth: `${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}`,
      listedEmployees: payrollEligibleEmployees.map((e) => ({
        id: e.id,
        empCode: e.empCode || e.employeeCode || "",
        name: [e.user?.name, e.user?.lastName].filter(Boolean).join(" "),
        ctc: Number(e.ctc || e.ctcAmount || 0) || 0,
        departmentId: e.departmentId || e.department?.id || "",
        branchId: e.branchId || e.branch?.id || "",
        designationId: e.designationId || e.designation?.id || "",
        gradeId: e.gradeId || e.grade?.id || "",
        categoryId: e.categoryId || e.category?.id || "",
        subDepartmentId: e.subDepartmentId || e.subDepartment?.id || "",
        employmentType: e.employmentType || "",
        employmentStatus: e.employmentStatus || "",
        joiningDate: e.joiningDate || e.dateOfJoin || e.joinDate || null,
        relievedDate: e.relievedDate || e.lastWorkingDay || null,
        location: e.location || e.workLocation || "",
        pfApplicable: e.pfApplicable,
        ceilingPf: e.ceilingPf,
        esiApplicable: e.esiApplicable,
        ptApplicable: e.ptApplicable,
        gratuityApplicable: e.gratuityApplicable,
      })),
      listedEmployeeIds: payrollEligibleEmployees.map((e) => e.id),
      listedEmployeeCount: payrollEligibleEmployees.length,
      gridVisibleCount: assignedStructureEmployees.length,
      companyEmployeeTotal: Number(totalEmployees) || allEmployees.length || 0,
      selectedEmployeeIds: payrollSelectedEmployeeIds,
      filterParams: appliedFilters,
      structureFilterIds: selectedStructureFilterIds,
      breakdownByEmployee: payrollBreakdownByEmployee,
      componentColumns: payrollComponentColumns,
      masterLists: {
        branchesList,
        departmentsList,
        designationsList,
        gradesList,
        categoriesList,
        subDepartmentsList,
      },
    }),
    [
      selectedYear,
      selectedMonth,
      payrollEligibleEmployees,
      assignedStructureEmployees.length,
      totalEmployees,
      allEmployees.length,
      payrollSelectedEmployeeIds,
      appliedFilters,
      selectedStructureFilterIds,
      payrollBreakdownByEmployee,
      payrollComponentColumns,
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
    ]
  );

  const togglePayrollEmployeeSelected = (employeeId) => {
    setPayrollSelectedEmployeeIds((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const toggleAllPayrollEmployeesSelected = () => {
    const pageIds = assignedStructureEmployees.map((e) => e.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => payrollSelectedEmployeeIds.includes(id));
    if (allSelected) {
      setPayrollSelectedEmployeeIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setPayrollSelectedEmployeeIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const payrollTableMinWidth = useMemo(() => {
    const base =
      (columnWidths.actions || 48) +
      (columnWidths.select || 40) +
      (columnWidths.slNo || 60) +
      (columnWidths.empCode || 120) +
      (columnWidths.name || 200) +
      (columnWidths.designation || 150) +
      (columnWidths.department || 150) +
      (columnWidths.joiningDate || 120) +
      (columnWidths.exitDate || 120) +
      (columnWidths.ctc || 120) +
      (columnWidths.salaryStructure || 160) +
      (columnWidths.taxRegime || 100) +
      (columnWidths.status || 120) +
      120 + // Gross
      120; // Net Pay
    return base + payrollComponentColumns.length * 120;
  }, [columnWidths, payrollComponentColumns]);

  /** Row count for list table viewport height (header + data rows) */
  const tableRowCount =
    itemsPerPage === "all"
      ? displayEmployees.length
      : Math.min(parseInt(itemsPerPage, 10) || 10, displayEmployees.length);

  // Tax regime counts - use filteredEmployees to show filtered count when filters are applied
  const oldRegimeCount = useMemo(() => {
    return filteredEmployees.filter((emp) => {
      const header = investmentHeaders[emp.id];
      const regimeRaw = header?.taxRegime || emp.taxRegime || "NEW";
      const regime = regimeRaw.toString().toUpperCase();
      return regime === "OLD";
    }).length;
  }, [filteredEmployees, investmentHeaders]);
  
  const newRegimeCount = useMemo(() => {
    return filteredEmployees.filter((emp) => {
      const header = investmentHeaders[emp.id];
      const regimeRaw = header?.taxRegime || emp.taxRegime || "NEW";
      const regime = regimeRaw.toString().toUpperCase();
      return regime !== "OLD";
    }).length;
  }, [filteredEmployees, investmentHeaders]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? "↑" : "↓";
  };

  // Template download function
  const handleTemplateDownload = () => {
    if (!canExportPayroll) {
      toast.error("You do not have permission to download templates.");
      return;
    }
    try {
      // Generate unified template data with both tax regimes
      const templateData = generateUnifiedTemplateData();

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet([templateData]);

      // Set column widths for all columns
      const colWidths = [
        { wch: 8 }, // Sl No
        { wch: 15 }, // Employee Code
        { wch: 25 }, // Employee Name
        { wch: 12 }, // Tax Regime
        // Old Regime columns
        { wch: 20 }, // HRA fields (Old)
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 }, // 80C fields (Old)
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 }, // VIA fields (Old)
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 }, // House Property fields (Old)
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        // New Regime columns
        { wch: 20 }, // HRA fields (New)
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 }, // 80C fields (New)
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 }, // VIA fields (New)
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 }, // House Property fields (New)
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        // Additional fields
        { wch: 15 },
        { wch: 18 },
        { wch: 15 },
        { wch: 50 }, // Remarks
      ];
      ws["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Investment Declaration Template");

      // Generate filename with current date
      const filename = buildPayrollExcelFilename(
        "Investment_Declaration_Template_Unified"
      );

      // Save file
      XLSX.writeFile(wb, filename);

      console.log("Unified template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Error downloading template. Please try again.");
    }
  };

  const handleBulkImport = (event) => {
    if (!canImportPayroll) {
      toast.error("You do not have permission to import payroll data.");
      if (event?.target) event.target.value = "";
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
      "text/csv", // .csv
    ];

    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid Excel file (.xlsx, .xls) or CSV file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Get the first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          toast.error("The Excel file appears to be empty or has no data rows.");
          return;
        }

        // Get headers (first row)
        const headers = jsonData[0];

        // Validate required headers
        const requiredHeaders = [
          "Employee Code",
          "Employee Name",
          "Tax Regime",
        ];

        const missingHeaders = requiredHeaders.filter(
          (header) => !headers.includes(header)
        );

        if (missingHeaders.length > 0) {
          toast.error(`Missing required columns: ${missingHeaders.join(", ")}`);
          return;
        }

        // Process data rows
        const processedData = [];
        const errors = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row.length === 0) continue; // Skip empty rows

          try {
            const employeeCode = row[headers.indexOf("Employee Code")];
            const employeeName = row[headers.indexOf("Employee Name")];
            const taxRegime = row[headers.indexOf("Tax Regime")];

            if (!employeeCode) {
              errors.push(`Row ${i + 1}: Employee Code is required`);
              continue;
            }

            // Find employee by code
            const employee = filteredEmployees.find(
              (emp) => emp.empCode === employeeCode
            );
            if (!employee) {
              errors.push(
                `Row ${i + 1}: Employee with code "${employeeCode}" not found`
              );
              continue;
            }

            // Prepare investment declaration data
            const declarationData = {
              employeeId: employee.id,
              employeeCode: employeeCode,
              taxRegime: taxRegime?.toLowerCase() || "old",

              // HRA Section - Updated to match actual form fields
              houseRent: {
                cityType: row[headers.indexOf("HRA - City Type")] || "metro",
                rentPaid: row[headers.indexOf("HRA - Rent Paid")] || "",
                landlordPan: row[headers.indexOf("HRA - Landlord PAN")] || "",
                address: row[headers.indexOf("HRA - Address")] || "",
              },

              // Section 80C - Updated to match actual form fields
              section80C: {
                lifeInsurance:
                  row[headers.indexOf("80C - Life Insurance Premium")] || "",
                ppf: row[headers.indexOf("80C - PPF Contribution")] || "",
                vpf: row[headers.indexOf("80C - VPF Contribution")] || "",
                pensionFund: row[headers.indexOf("80C - Pension Fund")] || "",
                nsc: row[headers.indexOf("80C - NSC Investment")] || "",
                nscInterest: row[headers.indexOf("80C - NSC Interest")] || "",
                ulip: row[headers.indexOf("80C - ULIP Investment")] || "",
                elss: row[headers.indexOf("80C - ELSS Investment")] || "",
                tuitionFees: row[headers.indexOf("80C - Tuition Fees")] || "",
                houseLoanPrincipal:
                  row[headers.indexOf("80C - Principal Repayment")] || "",
                registrationCharges:
                  row[headers.indexOf("80C - Registration Charges")] || "",
                sukanyaSamriddhi:
                  row[headers.indexOf("80C - Sukanya Samriddhi")] || "",
                infrastructureBonds:
                  row[headers.indexOf("80C - Infrastructure Bonds")] || "",
                fixedDeposit: row[headers.indexOf("80C - Fixed Deposit")] || "",
              },

              // VIA Deductions - Updated to match actual form fields
              sectionVIA: {
                section80D: {
                  self:
                    row[
                      headers.indexOf("VIA - Self Medical Insurance (80D)")
                    ] || "",
                  parents:
                    row[
                      headers.indexOf("VIA - Parents Medical Insurance (80D)")
                    ] || "",
                  parentsSenior:
                    row[
                      headers.indexOf("VIA - Senior Citizen Parents (80D)")
                    ] || "",
                  preventiveHealthCheckup:
                    row[headers.indexOf("VIA - Preventive Health Checkup")] ||
                    "",
                },
                otherInvestments: {
                  section80E:
                    row[
                      headers.indexOf("VIA - Education Loan Interest (80E)")
                    ] || "",
                  section80CCD1B:
                    row[headers.indexOf("VIA - NPS Additional (80CCD1B)")] ||
                    "",
                  section80EE:
                    row[headers.indexOf("VIA - Home Loan Interest (80EE)")] ||
                    "",
                  section80EEA:
                    row[headers.indexOf("VIA - Home Loan Interest (80EEA)")] ||
                    "",
                  section80EEB:
                    row[headers.indexOf("VIA - Home Loan Interest (80EEB)")] ||
                    "",
                  section80G:
                    row[headers.indexOf("VIA - Donations (80G)")] || "",
                  section80TTA:
                    row[headers.indexOf("VIA - Savings Interest (80TTA)")] ||
                    "",
                  section80TTB:
                    row[headers.indexOf("VIA - Deposit Interest (80TTB)")] ||
                    "",
                },
              },

              // House Property - Updated to match actual form fields
              houseProperty: {
                propertyType:
                  row[headers.indexOf("House Property - Property Type")] ||
                  "self-occupied",
                loanTakenBeforeApril1999:
                  row[
                    headers.indexOf("House Property - Loan Before April 1999")
                  ] === "Yes",
                interest:
                  row[headers.indexOf("House Property - Interest Paid")] || "",
                rentalIncome:
                  row[headers.indexOf("House Property - Rental Income")] || "",
                address: row[headers.indexOf("House Property - Address")] || "",
              },

              // Additional fields
              totalDeductions: row[headers.indexOf("Total Deductions")] || "",
              status: row[headers.indexOf("Declaration Status")] || "Submitted",
              remarks: row[headers.indexOf("Remarks")] || "",
              lastUpdated: new Date().toISOString(),
            };

            processedData.push(declarationData);
          } catch (error) {
            errors.push(`Row ${i + 1}: ${error.message}`);
          }
        }

        // Show results
        if (errors.length > 0) {
          const errorMessage = `Import completed with ${
            errors.length
          } errors:\n\n${errors.slice(0, 10).join("\n")}${
            errors.length > 10 ? "\n... and more errors" : ""
          }`;
          toast.error(errorMessage, { duration: 10000 });
        }

        if (processedData.length > 0) {
          console.log("Processed investment declarations:", processedData);
          // TODO: Send processedData to backend API
          toast.success(
            `Successfully processed ${processedData.length} investment declarations. ${errors.length} errors occurred.`
          );
        } else {
          toast.error("No valid data found to import.");
        }
      } catch (error) {
        console.error("Error processing Excel file:", error);
        toast.error(
          "Error processing Excel file. Please check the file format and try again."
        );
      }
    };

    reader.readAsArrayBuffer(file);

    // Reset file input
    event.target.value = "";
  };

  const handleDownload = async () => {
    if (!canExportPayroll) {
      toast.error("You do not have permission to export payroll data.");
      return;
    }
    if (!selectedFinancialYear) {
      toast.error("Please select a financial year to export");
      return;
    }

    // Check if there are any old regime declarations before showing confirmation modal
    const hasOldRegimeData = Object.values(investmentHeaders).some(
      (header) => header && header.taxRegime === "OLD"
    );

    if (!hasOldRegimeData) {
      // No old regime data - show message directly without modal
      toast("No investment declarations found for this financial year", { icon: "ℹ️" });
      return;
    }

    // Show confirmation modal only if there's old regime data
    setShowDownloadConfirm(true);
  };

  const confirmDownload = async () => {
    if (!canExportPayroll) {
      toast.error("You do not have permission to export payroll data.");
      setShowDownloadConfirm(false);
      return;
    }
    try {
      setShowDownloadConfirm(false);
      
      // Pass taxRegime='OLD' to filter only old regime declarations
      const response = await payrollService.exportDeclarations(
        selectedFinancialYear,
        "OLD"
      );
      
      // Check if response is actually JSON (error response) instead of blob
      // When backend returns JSON error with responseType: 'blob', axios converts it to a blob
      // We need to check if it's actually JSON by trying to parse it
      if (response.data instanceof Blob) {
        // Clone the blob before reading (since reading consumes it)
        const blobClone = response.data.slice();
        const text = await blobClone.text();
        
        try {
          const jsonData = JSON.parse(text);
          // If it parses as JSON and has success: false, it's an error response
          if (jsonData.success === false && jsonData.message) {
            toast(jsonData.message, { icon: "ℹ️" });
            return;
          }
        } catch {
          // Not JSON, proceed with blob download
        }
        
        // It's a valid blob, proceed with download
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildPayrollExcelFilename(
          "Investment_Declarations_Old_Regime"
        );
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        console.log("Investment declarations exported successfully from backend");
        toast.success("Old Regime investment declarations downloaded successfully");
      }
    } catch (error) {
      console.error("Error exporting investment declarations:", error);
      
      // Check if error response contains a message (when backend returns JSON instead of blob)
      let errorMessage = "Error exporting investment declarations. Please try again.";
      
      if (error.response) {
        // Try to parse the error response
        try {
          // If response.data is a Blob (from axios with responseType: 'blob'), try to read it as text
          if (error.response.data instanceof Blob) {
            const text = await error.response.data.text();
            try {
              const parsed = JSON.parse(text);
              if (parsed.message) {
                errorMessage = parsed.message;
              }
            } catch {
              // Not JSON, use default message
            }
          } else if (error.response.data && typeof error.response.data === 'object') {
            errorMessage = error.response.data.message || errorMessage;
          } else if (typeof error.response.data === 'string') {
            // If it's a string, try to parse it as JSON
            try {
              const parsed = JSON.parse(error.response.data);
              errorMessage = parsed.message || errorMessage;
            } catch {
              // If parsing fails, check if it contains the expected message
              if (error.response.data.includes("No investment declarations found")) {
                errorMessage = "No investment declarations found for this financial year";
              }
            }
          }
        } catch (parseError) {
          console.error("Error parsing error response:", parseError);
        }
      }
      
      // Show the message (info toast for "no data", error toast for actual errors)
      if (errorMessage.includes("No investment declarations found")) {
        toast(errorMessage, { icon: "ℹ️" });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleBulkApprove = () => {
    if (!canApprovePayroll) {
      toast.error("You do not have permission to approve payroll actions.");
      return;
    }
    // TODO: Implement bulk approve logic
    console.log("Bulk approving declarations");
    toast("Bulk approve functionality to be implemented.", { icon: "ℹ️" });
  };

  const handleViewEdit = (employee, mode) => {
    setSelectedEmployee(employee);
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const calculateDropdownPosition = (buttonRect) => {
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const estimatedMenuHeight = 280;
    let top = buttonRect.bottom + scrollY + 4;
    let left = buttonRect.left + scrollX;
    if (top + estimatedMenuHeight > scrollY + viewportHeight) {
      top = buttonRect.top + scrollY - estimatedMenuHeight - 4;
    }
    const menuWidth = 200;
    if (left + menuWidth > viewportWidth + scrollX) {
      left = viewportWidth + scrollX - menuWidth - 10;
    }
    if (left < scrollX + 10) left = scrollX + 10;
    return { top, left };
  };

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (openActionDropdown && actionButtonRefs.current[openActionDropdown]) {
        const button = actionButtonRefs.current[openActionDropdown];
        const rect = button.getBoundingClientRect();
        setDropdownPosition(calculateDropdownPosition(rect));
      }
    };
    if (openActionDropdown) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [openActionDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionDropdown && !event.target.closest(".action-dropdown")) {
        setOpenActionDropdown(null);
      }
    };
    if (openActionDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openActionDropdown]);

  const handlePayrollRowAction = (action, employee) => {
    setOpenActionDropdown(null);
    switch (action) {
      case "assignedStructure": {
        const openAssignedStructure = async () => {
          const id = employee?.id;
          if (!id) {
            toast.error("Employee record is incomplete.");
            return;
          }
          const toastId = "assigned-structure-open";
          try {
            toast.loading("Loading salary structure…", { id: toastId });
            const res = await employeeService.getEmployee(id);
            const data = res?.data ?? res;
            const full = typeof data?.id !== "undefined" ? data : employee;
            // Merge list + detail so CTC / org tags / names stay complete for the eye modal
            setStructureEmployee({ ...employee, ...full });
            setStructureAutoOpenKey((k) => k + 1);
            toast.dismiss(toastId);
          } catch (err) {
            console.error("Assigned Structure load employee:", err);
            toast.error(
              err?.response?.data?.message ||
                err?.message ||
                "Failed to load employee for salary structure",
              { id: toastId }
            );
            // Still try with the selected row (normalized inside the modal)
            setStructureEmployee(employee);
            setStructureAutoOpenKey((k) => k + 1);
          }
        };
        openAssignedStructure();
        break;
      }
      case "salaryRevision":
        setRevisionEmployee(employee);
        break;
      case "updateStatutory":
        setStatutoryEmployee(employee);
        break;
      case "taxDeclaration":
        handleViewEdit(employee, "edit");
        break;
      case "loan":
        setLoanEmployee(employee);
        break;
      case "leaveEncashment":
        setEncashEmployee(employee);
        break;
      case "advance":
        toast("Advance management will open here.", { icon: "ℹ️" });
        break;
      case "reimbursement":
        setFlexiClaimsOpen(true);
        break;
      default:
        break;
    }
  };

  const handleExportAssignedStructureExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Assigned Structure");
      const headers = [
        "Sl No",
        "Emp Code",
        "Name",
        "Designation",
        "Department",
        "CTC",
        "Salary Structure",
        ...payrollComponentColumns,
        "Gross Salary",
        "Net Pay",
      ];
      ws.addRow(headers);
      ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      ws.getRow(1).eachCell((c) => {
        c.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF1E3A5F" },
        };
      });
      payrollEligibleEmployees.forEach((emp, idx) => {
        const bd = payrollBreakdownByEmployee.get(emp.id) || {};
        const amounts = bd.amountsByName || {};
        ws.addRow([
          idx + 1,
          emp.employeeCode || emp.empCode || "",
          [emp.firstName, emp.lastName].filter(Boolean).join(" "),
          emp.designation?.name || emp.designationName || "",
          emp.department?.name || emp.departmentName || "",
          Number(emp.ctc) || 0,
          bd.structureName || "—",
          ...payrollComponentColumns.map((n) => amounts[n] ?? ""),
          bd.grossPay ?? "",
          bd.netPay ?? "",
        ]);
      });
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Assigned_Structure_${selectedYear}_${selectedMonth + 1}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    }
  };

  const stickyTh = (left, z = 45) => ({
    position: "sticky",
    left,
    zIndex: z,
    background: darkMode ? "#3a3a3a" : "#e6eaef",
  });
  const stickyTd = (left, z = 30) => ({
    position: "sticky",
    left,
    zIndex: z,
    background: darkMode ? "#2C2C2C" : "#fafafa",
  });
  const freezeLeft = {
    actions: 0,
    select: columnWidths.actions || 48,
    slNo: (columnWidths.actions || 48) + (columnWidths.select || 40),
    empCode:
      (columnWidths.actions || 48) +
      (columnWidths.select || 40) +
      (columnWidths.slNo || 60),
    name:
      (columnWidths.actions || 48) +
      (columnWidths.select || 40) +
      (columnWidths.slNo || 60) +
      (columnWidths.empCode || 120),
  };

  const payrollListRowClasses = (index, isActive) => {
    const borderColor = darkMode
      ? hasActiveFilters
        ? "border-[hsl(var(--ds-secondary))]"
        : "border-[#4C4C4C]"
      : hasActiveFilters
        ? "border-[hsl(var(--ds-border))]"
        : "border-[#e4e8ee]";
    const rowBase = darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[#fafafa]";
    const activeClass = isActive ? "attendance-row-active" : "";
    return `employee-table-row ${borderColor} border-b ${rowBase} ${activeClass}`;
  };

  // Upload functionality (copied from FamilyDetailsForm.jsx)
  const handlePhotoUpload = async (employeeId, file) => {
    if (file) {
      // Validate file type - allow only images
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/bmp",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Only image files (JPG, PNG, GIF, BMP, WebP) are allowed");
        return;
      }

      try {
        setPhotoUploading((prev) => ({ ...prev, [employeeId]: true }));

        // For new employees without ID, store as base64 temporarily
        if (!employeeId) {
          const reader = new FileReader();
          reader.onloadend = () => {
            // Store in local state for display
            setPhotoModal({
              isOpen: true,
              photo: reader.result,
              fileName: file.name,
              employeeId: employeeId,
            });
            setPhotoUploading((prev) => ({ ...prev, [employeeId]: false }));
          };
          reader.readAsDataURL(file);
          return;
        }

        // Upload photo using service for existing employees
        const response = await employeeService.uploadEmployeePhoto(
          employeeId,
          file
        );

        if (response.success) {
          // Update employee data with the file path
          setPhotoModal({
            isOpen: true,
            photo: response.photoPath,
            fileName: file.name,
            employeeId: employeeId,
          });
          console.log(
            "Employee photo uploaded successfully:",
            response.photoPath
          );
          toast.success("Photo uploaded successfully!");
        } else {
          console.error("Employee photo upload failed:", response.message);
          toast.error("Photo upload failed: " + response.message);
        }
      } catch (error) {
        console.error("Error uploading employee photo:", error);
        toast.error("Error uploading photo: " + (error.message || "Unknown error"));
      } finally {
        setPhotoUploading((prev) => ({ ...prev, [employeeId]: false }));
      }
    }
  };

  const handleViewPhoto = (employee) => {
    const photoUrl = `${API_URL.replace(
      /\/api\/?$/,
      ""
    )}/uploads/employee-photos/${employee.user?.photo}`;
    if (employee.user?.photo) {
      // Check if it's a base64 string or file path
      if (
        typeof employee.user.photo === "string" &&
        employee.user.photo.startsWith("data:")
      ) {
        // Base64 photo (new upload)
        setPhotoModal({
          isOpen: true,
          photo: employee.user.photo,
          fileName: employee.user.name || "Employee Photo",
          employeeId: employee.id,
        });
      } else if (typeof employee.user.photo === "string") {
        // File path (uploaded to backend)
        setPhotoModal({
          isOpen: true,
          photo: photoUrl,
          fileName: employee.user.name || "Employee Photo",
          employeeId: employee.id,
        });
      } else {
        // File object (shouldn't happen after our changes, but keeping for safety)
        setPhotoModal({
          isOpen: true,
          photo: photoUrl,
          fileName: employee.user.name || "Employee Photo",
          employeeId: employee.id,
        });
      }
    }
  };

  const closePhotoModal = () => {
    setPhotoModal({
      isOpen: false,
      photo: null,
      fileName: "",
      employeeId: null,
    });
  };

  const downloadPhoto = () => {
    if (photoModal.photo) {
      const link = document.createElement("a");
      link.href = photoModal.photo;
      link.download = photoModal.fileName || "employee-photo";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Approval/Rejection functions
  const handleApprove = (employeeId) => {
    setDocumentStatus((prev) => ({
      ...prev,
      [employeeId]: "approved",
    }));
    addPayrollInsight({
      action: "Investment Declaration",
      employeeId,
      status: "Approved",
      pendingAt: "Payroll Team",
      approvedBy: "Payroll Manager",
    });
    // Clear any rejection reason
    setRejectReasons((prev) => {
      const newReasons = { ...prev };
      delete newReasons[employeeId];
      return newReasons;
    });
  };

  const handleReject = (employeeId, reason) => {
    setDocumentStatus((prev) => ({
      ...prev,
      [employeeId]: "rejected",
    }));
    addPayrollInsight({
      action: "Investment Declaration",
      employeeId,
      status: "Rejected",
      pendingAt: "Payroll Team",
      approvedBy: "Payroll Manager",
    });
    setRejectReasons((prev) => ({
      ...prev,
      [employeeId]: reason,
    }));
  };

  const handleRejectReasonChange = (employeeId, reason) => {
    setRejectReasons((prev) => ({
      ...prev,
      [employeeId]: reason,
    }));
  };

  // Exactly one sidebar item may show the active left border at a time.
  // Import/Reports/Statutory children take priority over their parents and over the content tab.
  const payrollNavSelection = (() => {
    if (salaryRevisionImportOpen) return "import:salary_revision";
    if (structureComponentImportOpen) return "import:structure_components";
    if (showTemplateModal) return "import:investment";
    if (sidebarImportOpen) return "import";
    if (activePayrollTab === "reports") {
      if (payrollReportsSubTabTouched) {
        return `reports:${activePayrollReportsSubTab}`;
      }
      return "reports";
    }
    if (activePayrollTab === "statutory_filing") {
      if (statutoryFilingSubTabTouched) {
        return `statutory:${activeStatutoryFilingSubTab}`;
      }
      return "statutory_filing";
    }
    return activePayrollTab;
  })();

  const closePayrollSideMenus = () => {
    setSidebarImportOpen(false);
    setReportsMenuOpen(false);
    setStatutoryFilingMenuOpen(false);
  };

  return (
    <div className="ds-page ds-readable-ui">
      <div className="ds-page-shell">
        <style>{`
          @keyframes blink-border {
            0%, 100% {
              border-color: rgb(59 130 246);
              box-shadow: 0 0 0 0 rgba(209, 22, 22, 0.7);
            }
            50% {
              border-color: rgb(37 99 235);
              box-shadow: 0 0 0 4px rgba(236, 9, 9, 0);
            }
          }
        `}</style>
        {/* Sub Sidebar — shell matches Employee.jsx */}
        <div className="ds-page-body">
          <aside className="ds-page-aside">
            <div className="ds-page-aside-head">
              <h4 className="ds-small invisible uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]" aria-hidden="true">
                Payroll
              </h4>
            </div>
            <div className="ds-page-aside-nav">
              {canViewPayrollDashboard && (
              <button
                type="button"
                className={payrollNavSelection === "dashboard"
                    ? "ds-page-nav-item ds-page-nav-item-active"
                    : "ds-page-nav-item"}
                onClick={() => {
                  setActivePayrollTab("dashboard");
                  setShowPayrollInsights(false);
                  closePayrollSideMenus();
                }}
              >
                Dashboard
              </button>
              )}
              {canViewChecklistTasks && (
                <button
                  type="button"
                  className={payrollNavSelection === "checklist_tasks"
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"}
                  onClick={() => {
                    setActivePayrollTab("checklist_tasks");
                    setShowPayrollInsights(false);
                    closePayrollSideMenus();
                  }}
                >
                  Checklist Tasks
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setSidebarImportOpen((p) => !p);
                  setReportsMenuOpen(false);
                  setStatutoryFilingMenuOpen(false);
                }}
                className={payrollNavSelection === "import"
                    ? "ds-page-nav-item ds-page-nav-item-active"
                    : "ds-page-nav-item"}
              >
                Import
              </button>
              {sidebarImportOpen && (
                <div className="ds-page-nav-group">
                  <button
                type="button"
                    onClick={() => {
                      if (!canReviseSalary) {
                        toast.error("You do not have permission to import salary revisions.");
                        return;
                      }
                      setShowTemplateModal(false);
                      setStructureComponentImportOpen(false);
                      setSalaryRevisionImportOpen(true);
                    }}
                    disabled={!canReviseSalary}
                    className={`${
                      payrollNavSelection === "import:salary_revision"
                        ? "ds-page-nav-item ds-page-nav-item-active ds-page-nav-child"
                        : "ds-page-nav-item ds-page-nav-child"
                    }${!canReviseSalary ? " cursor-not-allowed opacity-45" : ""}`}
                  >
                    Salary Revision
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!canImportStructureComponents) {
                        toast.error(
                          "You do not have permission to import structure component values."
                        );
                        return;
                      }
                      setShowTemplateModal(false);
                      setSalaryRevisionImportOpen(false);
                      setStructureComponentImportOpen(true);
                    }}
                    disabled={!canImportStructureComponents}
                    className={`${
                      payrollNavSelection === "import:structure_components"
                        ? "ds-page-nav-item ds-page-nav-item-active ds-page-nav-child"
                        : "ds-page-nav-item ds-page-nav-child"
                    }${!canImportStructureComponents ? " cursor-not-allowed opacity-45" : ""}`}
                  >
                    Structure Components
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!canImportPayroll) {
                        toast.error("You do not have permission to import payroll data.");
                        return;
                      }
                      setSalaryRevisionImportOpen(false);
                      setStructureComponentImportOpen(false);
                      setShowTemplateModal(true);
                    }}
                    disabled={!canImportPayroll}
                    className={`${
                      payrollNavSelection === "import:investment"
                        ? "ds-page-nav-item ds-page-nav-item-active ds-page-nav-child"
                        : "ds-page-nav-item ds-page-nav-child"
                    }${!canImportPayroll ? " cursor-not-allowed opacity-45" : ""}`}
                  >
                    Investment Declaration Import
                  </button>
                </div>
              )}

              <button
                type="button"
                className={payrollNavSelection === "run_payroll"
                    ? "ds-page-nav-item ds-page-nav-item-active"
                    : "ds-page-nav-item"}
                onClick={() => {
                  setActivePayrollTab("run_payroll");
                  setRunPayrollPanelView("actions");
                  setShowPayrollInsights(false);
                  closePayrollSideMenus();
                }}
              >
                Payroll Overview
              </button>
              <button
                type="button"
                className={payrollNavSelection === "reports"
                    ? "ds-page-nav-item ds-page-nav-item-active"
                    : "ds-page-nav-item"}
                onClick={() => {
                  setActivePayrollTab("reports");
                  setShowPayrollInsights(false);
                  setSidebarImportOpen(false);
                  setStatutoryFilingMenuOpen(false);
                  setPayrollReportsSubTabTouched(false);
                  setReportsMenuOpen((prev) => (activePayrollTab === "reports" ? !prev : true));
                }}
              >
                Reports
              </button>
              {reportsMenuOpen && (
                <div className="ds-page-nav-group">
                  {[
                    {
                      id: "salary-revision",
                      label: "Salary Revision Report",
                      show: canViewPayrollReports && (isAdmin || hasScreenAccess("payroll", "salary_revision_report", "view")),
                    },
                    {
                      id: "unpaid-salary",
                      label: "Unpaid Salary",
                      show: canViewUnpaidSalaryReport,
                    },
                    {
                      id: "form-16",
                      label: "Form-16",
                      show: canViewForm16Report,
                    },
                  ]
                    .filter((report) => report.show)
                    .map((report) => (
                    <button
                      key={report.id}
                      type="button"
                      onClick={() => {
                        setActivePayrollTab("reports");
                        setSidebarImportOpen(false);
                        setPayrollReportsSubTabTouched(true);
                        setActivePayrollReportsSubTab(report.id);
                      }}
                      className={
                        payrollNavSelection === `reports:${report.id}`
                          ? "ds-page-nav-item ds-page-nav-item-active ds-page-nav-child"
                          : "ds-page-nav-item ds-page-nav-child"
                      }
                    >
                      {report.label}
                    </button>
                  ))}
                </div>
              )}

              {canViewStatutoryFiling && (
                <>
              <button
                type="button"
                    className={payrollNavSelection === "statutory_filing"
                        ? "ds-page-nav-item ds-page-nav-item-active"
                        : "ds-page-nav-item"}
                    onClick={() => {
                      setActivePayrollTab("statutory_filing");
                      setShowPayrollInsights(false);
                      setSidebarImportOpen(false);
                      setReportsMenuOpen(false);
                      setStatutoryFilingSubTabTouched(false);
                      setStatutoryFilingMenuOpen((prev) =>
                        activePayrollTab === "statutory_filing" ? !prev : true
                      );
                    }}
                  >
                    Statutory Filing
              </button>
                  {statutoryFilingMenuOpen && (
                    <div className="ds-page-nav-group">
                      {[
                        { id: "pf", label: "PF" },
                        { id: "esi", label: "ESI" },
                        { id: "pt", label: "PT" },
                        { id: "nps", label: "NPS" },
                        { id: "meal", label: "Meal Voucher" },
                        ...(canViewForm24Q
                          ? [{ id: "form-24q", label: "e-TDS Return" }]
                          : []),
                      ].map((item) => (
                  <button
                          key={item.id}
                    type="button"
                    onClick={() => {
                            setActivePayrollTab("statutory_filing");
                            setSidebarImportOpen(false);
                            selectStatutoryFilingSubTab(item.id);
                          }}
                          className={
                            payrollNavSelection === `statutory:${item.id}`
                              ? "ds-page-nav-item ds-page-nav-item-active ds-page-nav-child"
                              : "ds-page-nav-item ds-page-nav-child"
                          }
                  >
                          {item.label}
                  </button>
                      ))}
                </div>
                  )}
                </>
              )}

              <button
                type="button"
                onClick={handleDownload}
                disabled={!canExportPayroll}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                  "ds-page-nav-item"
                }`}
              >
                Export
              </button>

              {canOpenPayrollSettings && (
                <button
                  type="button"
                  onClick={() => {
                    setActivePayrollTab("settings");
                    closePayrollSideMenus();
                    setShowSettingsModal(false);
                    loadPayrollSettings();
                  }}
                  className={payrollNavSelection === "settings"
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"}
                >
                  Settings
                </button>
              )}
            </div>

          </aside>
          {/* Tab content — flex sibling; aside width reserved in layout (no pl- offset needed) */}
          <div className="ds-page-main">
          <div className="ds-page-content-flush">

        {/* Payroll Tab */}
        {!canViewAnyPayrollTab && (
          <div className="flex flex-1 items-center justify-center p-6">
            <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              You do not have access to Payroll for this role.
            </div>
          </div>
        )}
        {activePayrollTab === "dashboard" && canViewPayrollDashboard && (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
            {false && (
            <ActionInsightsPanel
              darkMode={darkMode}
              title="Payroll Action Insights"
              items={payrollInsightItems}
              isFlipped={showPayrollInsights}
              onFlip={() => setShowPayrollInsights((v) => !v)}
              showFlipButton={false}
              overlayMode="sibling-hide"
            />
            )}
            {!showPayrollInsights && (
              <div className="ds-page-content-flush">
                <div className="ds-page-panel min-h-0 flex-1 overflow-hidden">
                  <PayrollDashboardPanel
                    darkMode={darkMode}
                    showFlipButton={false}
                    employees={filteredEmployees}
                    breakdownByEmployee={payrollBreakdownByEmployee}
                    structures={salaryStructures}
                    insightItems={payrollInsightItems}
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    oldRegimeCount={oldRegimeCount}
                    newRegimeCount={newRegimeCount}
                    loading={isLoading}
                    canExport={canExportPayroll}
                    onRefresh={() => setMountTrigger((p) => p + 1)}
                    onPeriodChange={(p) => {
                      if (!p || p.periodMode !== "month") return;
                      const nextYear = Number(p.year);
                      const nextMonth = Number(p.month) - 1;
                      if (
                        !Number.isFinite(nextYear) ||
                        !Number.isFinite(nextMonth) ||
                        nextMonth < 0 ||
                        nextMonth > 11
                      ) {
                        return;
                      }
                      if (nextYear === selectedYear && nextMonth === selectedMonth) return;
                      setSelectedYear(nextYear);
                      setSelectedMonth(nextMonth);
                      setMonthPickerValue(getMonthRangeValue(nextYear, nextMonth));
                      setPage(1);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activePayrollTab === "run_payroll" && canViewPayrollWorkspace && (
          <div className="flex min-h-0 w-full flex-1 flex-col gap-2 overflow-hidden p-3">
            {canRunPayrollWizard ? (
              <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {runPayrollPanelView !== "actions" ? (
                    <h2 className="text-base font-semibold text-[hsl(var(--ds-secondary))] dark:text-[hsl(var(--ds-fg))]">
                      Payroll Overview
                    </h2>
                  ) : null}
                  <div
                    className={`${
                      runPayrollPanelView === "actions" ? "" : "mt-2 "
                    }inline-flex rounded-full border border-[hsl(var(--ds-border))] p-0.5`}
                  >
                    <button
                      type="button"
                      onClick={() => setRunPayrollPanelView("actions")}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                        runPayrollPanelView === "actions"
                          ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                          : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      Actions
                    </button>
                    <button
                      type="button"
                      onClick={() => setRunPayrollPanelView("run")}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                        runPayrollPanelView === "run"
                          ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                          : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      Run Payroll
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {runPayrollPanelView === "actions" || !canRunPayrollWizard ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <PayrollActionsHub
                darkMode={darkMode}
                payMonth={`${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}`}
                canActionScreen={canPayrollActionScreen}
                onAssignedStructure={
                  canPayrollActionScreen("pay_act_assigned_structure", "view")
                    ? (emp) => handlePayrollRowAction("assignedStructure", emp)
                    : null
                }
                onSalaryRevision={
                  canPayrollActionScreen("pay_act_salary_revision", "view")
                    ? (emp) => handlePayrollRowAction("salaryRevision", emp)
                    : null
                }
                onUpdateStatutory={
                  canPayrollActionScreen("pay_act_update_statutory", "view")
                    ? (emp) => handlePayrollRowAction("updateStatutory", emp)
                    : null
                }
                onTaxPlanning={
                  canPayrollActionScreen("pay_act_tax_planning", "view")
                    ? (emp) => handlePayrollRowAction("taxDeclaration", emp)
                    : null
                }
                onLoan={
                  canPayrollActionScreen("pay_act_loan", "view")
                    ? (emp) => handlePayrollRowAction("loan", emp)
                    : null
                }
                onLeaveEncashment={
                  canPayrollActionScreen("pay_act_leave_encashment", "view")
                    ? (emp) => handlePayrollRowAction("leaveEncashment", emp)
                    : null
                }
                onAdvance={
                  canPayrollActionScreen("pay_act_advance", "view")
                    ? (emp) => handlePayrollRowAction("advance", emp)
                    : null
                }
                onReimbursement={
                  canPayrollActionScreen("pay_act_reimbursement", "view")
                    ? (emp) => handlePayrollRowAction("reimbursement", emp)
                    : null
                }
              />
              </div>
            ) : (
              <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden pt-0.5">
                <RunPayrollWizard
                  darkMode={darkMode}
                  scopeContext={payrollScopeContext}
                  canApprove={canApprovePayroll}
                  salaryStructures={salaryStructures}
                  statutoryConfigJson={statutoryConfigJson}
                  branchesList={branchesList}
                  onPayMonthChange={(ym) => {
                    const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
                    if (!m) return;
                    const nextYear = Number(m[1]);
                    const nextMonth = Number(m[2]) - 1;
                    if (nextYear === selectedYear && nextMonth === selectedMonth) return;
                    setSelectedYear(nextYear);
                    setSelectedMonth(nextMonth);
                    setMonthPickerValue(getMonthRangeValue(nextYear, nextMonth));
                    setPage(1);
                  }}
                />
              </div>
            )}
          </div>
        )}

        {activePayrollTab === "checklist_tasks" && canViewChecklistTasks && (
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            <PayrollChecklistEditor darkMode={darkMode} canEdit={canEditChecklistTasks} />
          </div>
        )}

        {(activePayrollTab === "assigned_structure" || activePayrollTab === "payroll") &&
          canViewPayrollWorkspace && (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
            {false && (
            <ActionInsightsPanel
              darkMode={darkMode}
              title="Payroll Action Insights"
              items={payrollInsightItems}
              isFlipped={showPayrollInsights}
              onFlip={() => setShowPayrollInsights((v) => !v)}
              showFlipButton={false}
              overlayMode="sibling-hide"
            />
            )}

            {!showPayrollInsights && (
            <div
              className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-3 ${
                darkMode ? "bg-[hsl(var(--ds-surface))]" : "bg-[hsl(var(--ds-surface))]"
              }`}
            >
            <style>{`
              @keyframes blink-border {
                0%, 100% {
                  border-color: rgb(255 54 54);
                  box-shadow: 0 0 0 0 rgba(255, 54, 54, 0.35);
                }
                50% {
                  border-color: rgb(216 74 70);
                  box-shadow: 0 0 0 4px rgba(255, 54, 54, 0);
                }
              }
            `}</style>
            <div
              className={`shrink-0 px-3 pt-2.5 pb-3 ${
                darkMode
                  ? "bg-[hsl(var(--ds-bg))] border-[hsl(var(--ds-border))] rounded-t-2xl"
                  : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] rounded-t-2xl"
              } border z-50 shadow-sm`}
            >
            <div className="mt-2.5 flex flex-nowrap items-center gap-2.5 overflow-x-auto">
              <div className="relative w-full min-w-[240px] max-w-md shrink-0">
                <div className="relative">
                  <Search
                    className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                    } w-3.5 h-3.5`}
                  />
                  <input
                    type="search"
                    name="employee_filter_query"
                    id="admin-payroll-employee-filter"
                    role="searchbox"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-lpignore="true"
                    placeholder="Search by employee code, name, or mobile number"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full rounded-full border py-2 pl-8 pr-8 text-xs transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/30 ${
                      darkMode
                        ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] focus:border-[hsl(var(--ds-primary))]"
                        : "border-[#f3d6bf] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] focus:border-[#ef6f2e] hover:border-[#ef6f2e]"
                    } shadow-sm`}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        setDebouncedSearchTerm("");
                      }}
                      className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                        darkMode
                          ? "text-[hsl(var(--ds-muted-fg))] hover:text-gray-200"
                          : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                      }`}
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="ml-auto flex flex-nowrap items-center gap-2.5 shrink-0">
                {shouldShowIncomeTaxGuide && (
                  <button
                    type="button"
                    onClick={openIncomeTaxGuidePreview}
                    className={`shrink-0 px-2.5 py-1.5 text-xs border rounded-md transition-all ${
                      darkMode
                        ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[#3C3C3C]"
                        : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    }`}
                    title="Open Income Tax Guide"
                  >
                    Income Tax Guide
                  </button>
                )}
                <div className="flex items-center space-x-1.5 shrink-0">
                  <label
                    className={`text-xs font-medium whitespace-nowrap ${
                      darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"
                    }`}
                  >
                    Month:
                  </label>
                  <DatePicker
                    compact
                    darkMode={darkMode}
                    monthYearOnly
                    showMonthName
                    key={monthPickerValue}
                    value={monthPickerValue}
                    onChange={handleMonthRangeChange}
                    min={minDate}
                    max={new Date()}
                  />
                </div>

                {/* View Mode Toggle */}
                <button
                  onClick={() =>
                    setViewMode(viewMode === "list" ? "grid" : "list")
                  }
                  className={`px-2.5 py-1.5 border rounded-md text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md ${
                    darkMode
                      ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[#3C3C3C]"
                      : "bg-[hsl(var(--ds-surface))]/95 border-[#f3d6bf] text-[hsl(var(--ds-fg))] hover:border-[#ef6f2e]"
                  }`}
                  title={`Switch to ${
                    viewMode === "list" ? "Grid" : "List"
                  } View`}
                >
                  {viewMode === "list" ? (
                    <LayoutGrid className="h-4 w-4" />
                  ) : (
                    <List className="h-4 w-4" />
                  )}
                </button>

                {/* Action Buttons */}
                {/* Bulk Import Button */}
                {/* <button
                  onClick={() => setShowTemplateModal(true)}
                  className={`inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 flex items-center space-x-2 rounded-md px-3 py-1.5 text-sm transition-all ${
                    darkMode ? "hover:opacity-95" : "hover:shadow-md"
                  }`}
                >
                  <FileUp className="h-4 w-4" />
                  <span>Bulk Import</span>
                </button> */}

                <button
                  type="button"
                  onClick={() => setShowFilterPopup(true)}
                  className={`flex items-center justify-center inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 text-sm px-3 py-1.5 transition-all ${
                    hasActiveFilters
                      ? "ring-2 ring-[hsl(var(--ds-primary))] ring-offset-1 dark:ring-offset-[#1C1C1C]"
                      : ""
                  }`}
                  style={
                    hasActiveFilters
                      ? { animation: "blink-border 1.5s ease-in-out infinite" }
                      : undefined
                  }
                  title="Filters"
                  aria-label="Filters"
                >
                  <Filter className="w-4 h-4" aria-hidden />
                </button>

                <div className="relative dropdown-container">
                  <button
                    type="button"
                    onClick={() => setStructureFilterOpen((v) => !v)}
                    className={`px-3 py-1.5 border rounded-md text-xs font-medium whitespace-nowrap ${
                      darkMode
                        ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                        : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                    } ${
                      selectedStructureFilterIds.length
                        ? "ring-1 ring-[hsl(var(--ds-primary))]"
                        : ""
                    }`}
                    title="Filter by salary structure"
                  >
                    {selectedStructureFilterIds.length === 0
                      ? "All Structures"
                      : selectedStructureFilterIds.length === 1
                        ? "1 Structure"
                        : `${selectedStructureFilterIds.length} Structures`}
                  </button>
                  {structureFilterOpen && (
                    <div
                      className={`absolute right-0 z-50 mt-1 max-h-64 w-64 overflow-y-auto rounded-md border shadow-lg ${
                        darkMode
                          ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                      }`}
                    >
                      <button
                        type="button"
                        className={`w-full px-3 py-2 text-left text-xs font-medium border-b ${
                          darkMode
                            ? "border-[hsl(var(--ds-border))] hover:bg-[#3C3C3C]"
                            : "border-gray-100 hover:bg-[hsl(var(--ds-muted))]"
                        }`}
                        onClick={() => {
                          setSelectedStructureFilterIds([]);
                          setStructureFilterOpen(false);
                        }}
                      >
                        All Structures
                      </button>
                      {(salaryStructures || []).map((s) => {
                        const checked = selectedStructureFilterIds
                          .map(String)
                          .includes(String(s.id));
                        return (
                          <label
                            key={s.id}
                            className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-xs ${
                              darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-[hsl(var(--ds-muted))]"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setSelectedStructureFilterIds((prev) => {
                                  const id = String(s.id);
                                  if (prev.map(String).includes(id)) {
                                    return prev.filter((x) => String(x) !== id);
                                  }
                                  return [...prev, s.id];
                                });
                              }}
                            />
                            <span className="truncate">{s.name || `Structure ${s.id}`}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleExportAssignedStructureExcel}
                  disabled={!canExportPayroll || !assignedStructureEmployees.length}
                  className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs font-medium ${
                    darkMode
                      ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[#3C3C3C]"
                      : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  } disabled:opacity-50`}
                  title="Export Assigned Structure to Excel"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export Excel
                </button>

                {payrollSelectedEmployeeIds.length > 0 && (
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                      darkMode ? "bg-[hsl(var(--ds-primary))]/30 text-[hsl(var(--ds-accent))]" : "bg-red-50 text-[hsl(var(--ds-secondary))]"
                    }`}
                    title="Use Selected employees only in Run Payroll → Scope"
                  >
                    {payrollSelectedEmployeeIds.length} selected for payroll
                  </span>
                )}

                {/* Investment Declaration Filters */}
                <div className="flex items-center space-x-2 dropdown-container">
                  {/* Tax Regime Filter */}
                  <div className="relative dropdown-container">
                    <button
                      onClick={() => {
                        setTaxRegimeDropdownOpen(!taxRegimeDropdownOpen);
                        setStatusDropdownOpen(false);
                      }}
                      className={`appearance-none px-3 py-1.5 pr-8 border rounded-none text-sm font-medium ${
                        darkMode
                          ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] focus:border-[hsl(var(--ds-primary))]"
                          : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] focus:border-[hsl(var(--ds-primary))]"
                      } transition-all duration-200 focus:outline-none shadow-sm hover:shadow-md cursor-pointer`}
                    >
                      {selectedTaxRegimes.length === 0
                        ? "Regime"
                        : selectedTaxRegimes.length === 1
                        ? selectedTaxRegimes[0] === "OLD"
                          ? "Old Regime"
                          : "New Regime"
                        : `${selectedTaxRegimes.length} selected`}
                      <div
                        className={`absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none ${
                          darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                        }`}
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {taxRegimeDropdownOpen && (
                      <div
                        className={`absolute z-50 mt-1 w-48 bg-[hsl(var(--ds-surface))] rounded-none shadow-lg border ${
                          darkMode
                            ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))]"
                            : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                        } right-0`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="py-1">
                          {selectedTaxRegimes.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedTaxRegimes([])}
                              className={`w-full flex items-center px-4 py-2 text-sm cursor-pointer border-b ${
                                darkMode
                                  ? "hover:bg-[#3C3C3C] text-red-400 hover:text-red-300 border-[hsl(var(--ds-border))]"
                                  : "hover:bg-[hsl(var(--ds-muted))] text-red-600 hover:text-red-700 border-[hsl(var(--ds-border))]"
                              }`}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Clear All
                            </button>
                          )}

                          {["OLD", "NEW"].map((regime) => (
                            <label
                              key={regime}
                              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                                darkMode
                                  ? "hover:bg-[#3C3C3C] text-[hsl(var(--ds-fg))]"
                                  : "hover:bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTaxRegimes.includes(regime)}
                                onChange={() => {
                                  setSelectedTaxRegimes((prev) =>
                                    prev.includes(regime)
                                      ? prev.filter((r) => r !== regime)
                                      : [...prev, regime]
                                  );
                                }}
                                className={`w-4 h-4 mr-2 rounded-none border-2 ${
                                  darkMode
                                    ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))]"
                                    : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-secondary))]"
                                } focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/35 focus:ring-offset-0 cursor-pointer`}
                              />
                              <span className="flex-1 cursor-pointer">
                                {regime === "OLD" ? "Old Regime" : "New Regime"}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Declaration Status Filter */}
                  <div className="relative dropdown-container">
                    <button
                      onClick={() => {
                        setStatusDropdownOpen(!statusDropdownOpen);
                        setTaxRegimeDropdownOpen(false);
                      }}
                      className={`appearance-none px-3 py-1.5 pr-8 border rounded-none text-sm font-medium ${
                        darkMode
                          ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] focus:border-[hsl(var(--ds-primary))]"
                          : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] focus:border-[hsl(var(--ds-primary))]"
                      } transition-all duration-200 focus:outline-none shadow-sm hover:shadow-md cursor-pointer`}
                    >
                      {selectedDeclarationStatuses.length === 0
                        ? "Status"
                        : selectedDeclarationStatuses.length === 1
                        ? selectedDeclarationStatuses[0] === "DRAFT"
                          ? "Draft"
                          : selectedDeclarationStatuses[0] === "DEFAULT"
                          ? "Default"
                          : selectedDeclarationStatuses[0].charAt(0) +
                            selectedDeclarationStatuses[0].slice(1).toLowerCase()
                        : `${selectedDeclarationStatuses.length} selected`}
                      <div
                        className={`absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none ${
                          darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                        }`}
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {statusDropdownOpen && (
                      <div
                        className={`absolute z-50 mt-1 w-48 bg-[hsl(var(--ds-surface))] rounded-none shadow-lg border ${
                          darkMode
                            ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))]"
                            : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                        } right-0`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="py-1">
                          {selectedDeclarationStatuses.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedDeclarationStatuses([])}
                              className={`w-full flex items-center px-4 py-2 text-sm cursor-pointer border-b ${
                                darkMode
                                  ? "hover:bg-[#3C3C3C] text-red-400 hover:text-red-300 border-[hsl(var(--ds-border))]"
                                  : "hover:bg-[hsl(var(--ds-muted))] text-red-600 hover:text-red-700 border-[hsl(var(--ds-border))]"
                              }`}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Clear All
                            </button>
                          )}

                          {["DEFAULT", "DRAFT", "SUBMITTED", "APPROVED", "REVIEWED", "REJECTED"].map((status) => (
                            <label
                              key={status}
                              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                                darkMode
                                  ? "hover:bg-[#3C3C3C] text-[hsl(var(--ds-fg))]"
                                  : "hover:bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedDeclarationStatuses.includes(status)}
                                onChange={() => {
                                  setSelectedDeclarationStatuses((prev) =>
                                    prev.includes(status)
                                      ? prev.filter((s) => s !== status)
                                      : [...prev, status]
                                  );
                                }}
                                className={`w-4 h-4 mr-2 rounded-none border-2 ${
                                  darkMode
                                    ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))]"
                                    : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-secondary))]"
                                } focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/35 focus:ring-offset-0 cursor-pointer`}
                              />
                              <span className="flex-1 cursor-pointer">
                                {status === "DRAFT"
                                  ? "Draft"
                                  : status === "DEFAULT"
                                  ? "Default"
                                  : status.charAt(0) + status.slice(1).toLowerCase()}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Clear All Filters Button */}
                  {(selectedTaxRegimes.length > 0 || selectedDeclarationStatuses.length > 0) && (
                    <button
                      onClick={() => {
                        setSelectedTaxRegimes([]);
                        setSelectedDeclarationStatuses([]);
                      }}
                      className={`px-2.5 py-1.5 text-sm font-medium rounded-none transition-all duration-200 ${
                        darkMode
                          ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                          : "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                      } shadow-sm hover:shadow-md`}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>

                {/* Show Options */}
                <div className="flex items-center space-x-2">
                  <label
                    className={`text-xs font-medium ${
                      darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"
                    }`}
                  >
                    Show:
                  </label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      setItemsPerPage(
                        e.target.value === "all"
                          ? "all"
                          : parseInt(e.target.value)
                      )
                    }
                    className={`px-2 py-1.5 border rounded-md text-xs font-medium transition-all duration-200 focus:outline-none shadow-sm ${
                      darkMode
                        ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] focus:border-[hsl(var(--ds-primary))]"
                        : "bg-[hsl(var(--ds-surface))]/95 border-[#f3d6bf] text-[hsl(var(--ds-fg))] focus:border-[#ef6f2e]"
                    }`}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value="all">All</option>
                  </select>
                </div>

                {/* <button
                  onClick={handleBulkApprove}
                  className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm text-white hover:bg-amber-700"
                >
                  Bulk Approve
                </button> */}
              </div>
            </div>
            </div>

            {/* Employee Table/Grid Container — rounded-b-2xl shell; scroll only inside table area (no extra page scrollbar) */}
            <div
              className={`mt-0.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-2xl ${
                darkMode ? "border border-[hsl(var(--ds-border))]" : "border border-[hsl(var(--ds-border))]"
              }`}
            >
              <div className="payroll-list-container relative flex flex-1 min-h-0 flex-col overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--ds-primary))]"></div>
                    <p
                      className={`text-sm ${
                        darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      Loading employees...
                    </p>
                  </div>
                </div>
              ) : fetchError ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div
                      className={`p-4 rounded-full ${
                        darkMode ? "bg-red-900/20" : "bg-red-100"
                      }`}
                    >
                      <X
                        className={`h-8 w-8 ${
                          darkMode ? "text-red-400" : "text-red-600"
                        }`}
                      />
                    </div>
                    <div className="text-center">
                      <h3
                        className={`text-lg font-medium ${
                          darkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        Error Loading Employees
                      </h3>
                      <p
                        className={`text-sm mt-1 ${
                          darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                        }`}
                      >
                        {fetchError}
                      </p>
                      <button
                        onClick={() => window.location.reload()}
                        className={`mt-4 px-4 py-2 text-sm rounded-none ${
                          darkMode
                            ? "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                            : "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                        } transition-colors`}
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div
                      className={`p-4 rounded-full ${
                        darkMode ? "bg-[#3C3C3C]" : "bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      <Search
                        className={`h-8 w-8 ${
                          darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                        }`}
                      />
                    </div>
                    <div className="text-center">
                      <h3
                        className={`text-lg font-medium ${
                          darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"
                        }`}
                      >
                        No employees found
                      </h3>
                      <p
                        className={`text-sm mt-1 ${
                          darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                        }`}
                      >
                        {allEmployees.length === 0
                          ? "No employees available in the system"
                          : "Try adjusting your search or month filter"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
                  {/* List View — vertical scroll only in this region; avoids nested scrollbar on main */}
                  {viewMode === "list" && (
                    <div
                      className={`employee-table-scroll-area flex min-h-0 w-full min-w-0 flex-1 flex-col ${
                        darkMode ? "employee-table-scroll-area-dark" : ""
                      }`}
                    >
                      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-auto">
                        <table
                          className={`border-collapse text-sm leading-tight employee-table ${
                            darkMode ? "employee-table-dark" : ""
                          } ${
                            darkMode
                              ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                              : "bg-[#fafafa] text-[hsl(var(--ds-fg))]"
                          }`}
                          style={{ minWidth: `${payrollTableMinWidth}px`, width: "max-content" }}
                        >
                          <thead
                            className={`sticky top-0 z-40 border-b ${
                              darkMode
                                ? "border-[#4C4C4C] bg-[#3a3a3a]"
                                : "border-[#d7dee8] bg-[#e6eaef]"
                            }`}
                          >
                            <tr style={{ height: `${rowHeight}px` }}>
                              <th
                                className="px-2 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide"
                                style={{
                                  width: `${columnWidths.actions}px`,
                                  ...stickyTh(freezeLeft.actions, 46),
                                }}
                              >
                                Actions
                              </th>
                              <th
                                className="px-1 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide"
                                style={{
                                  width: `${columnWidths.select}px`,
                                  ...stickyTh(freezeLeft.select, 46),
                                }}
                                title="Select for Run Payroll scope"
                              >
                                <input
                                  type="checkbox"
                                  aria-label="Select all on page"
                                  checked={
                                    assignedStructureEmployees.length > 0 &&
                                    assignedStructureEmployees.every((e) =>
                                      payrollSelectedEmployeeIds.includes(e.id)
                                    )
                                  }
                                  onChange={toggleAllPayrollEmployeesSelected}
                                />
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{
                                  width: `${columnWidths.slNo}px`,
                                  ...stickyTh(freezeLeft.slNo, 46),
                                }}
                                onClick={() => handleSort("slNo")}
                              >
                                Sl No {getSortIcon("slNo")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{
                                  width: `${columnWidths.empCode}px`,
                                  ...stickyTh(freezeLeft.empCode, 46),
                                }}
                                onClick={() => handleSort("empCode")}
                              >
                                Emp Code {getSortIcon("empCode")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{
                                  width: `${columnWidths.name}px`,
                                  ...stickyTh(freezeLeft.name, 46),
                                }}
                                onClick={() => handleSort("name")}
                              >
                                Name {getSortIcon("name")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{
                                  width: `${columnWidths.designation}px`,
                                }}
                                onClick={() => handleSort("designation")}
                              >
                                Designation {getSortIcon("designation")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{
                                  width: `${columnWidths.department}px`,
                                }}
                                onClick={() => handleSort("department")}
                              >
                                Department {getSortIcon("department")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{
                                  width: `${columnWidths.joiningDate}px`,
                                }}
                                onClick={() => handleSort("dateOfJoin")}
                              >
                                DOJ {getSortIcon("dateOfJoin")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{ width: `${columnWidths.exitDate}px` }}
                                onClick={() => handleSort("exitDate")}
                              >
                                DOL {getSortIcon("exitDate")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{ width: `${columnWidths.ctc}px` }}
                                onClick={() => handleSort("ctc")}
                              >
                                CTC {getSortIcon("ctc")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide ${
                                  darkMode ? "" : ""
                                }`}
                                style={{
                                  width: `${columnWidths.salaryStructure || 160}px`,
                                  minWidth: 140,
                                }}
                                title="Salary structure assigned to the employee"
                              >
                                Salary Structure
                              </th>
                              {payrollComponentColumns.map((compName) => (
                                <th
                                  key={`hdr-comp-${compName}`}
                                  className="px-3 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide"
                                  style={{ width: 120, minWidth: 110 }}
                                  title={`${compName} (monthly — same as employee profile breakdown)`}
                                >
                                  {compName}
                                </th>
                              ))}
                              <th
                                className="px-3 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide"
                                style={{ width: 120, minWidth: 110 }}
                                title="Gross Salary (E + R) — same as employee profile breakdown"
                              >
                                Gross Salary
                              </th>
                              <th
                                className="px-3 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide"
                                style={{ width: 120, minWidth: 110 }}
                                title="Net Pay (E + R − D) — same as employee profile breakdown"
                              >
                                Net Pay
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                                style={{ width: `${columnWidths.taxRegime}px` }}
                                onClick={() => handleSort("taxRegime")}
                              >
                                Tax Regime {getSortIcon("taxRegime")}
                              </th>
                              <th
                                className="px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide"
                                style={{ width: `${columnWidths.status}px` }}
                              >
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignedStructureEmployees.map((employee, index) => (
                              <tr
                                key={employee.id}
                                className={payrollListRowClasses(
                                  index,
                                  hoveredRowIndex !== null && hoveredRowIndex === index
                                )}
                                style={{ height: `${rowHeight}px` }}
                                onMouseEnter={() => setHoveredRowIndex(index)}
                                onMouseLeave={() => setHoveredRowIndex(null)}
                                onDoubleClick={() => handleViewEdit(employee, "edit")}
                              >
                                  <td
                                    className="px-1 py-1 text-center align-middle"
                                    style={{
                                      width: `${columnWidths.actions}px`,
                                      ...stickyTd(freezeLeft.actions),
                                    }}
                                  >
                                    <div className="relative action-dropdown">
                                      <button
                                        ref={(el) => {
                                          if (el) actionButtonRefs.current[employee.id] = el;
                                        }}
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const button = e.currentTarget;
                                          const rect = button.getBoundingClientRect();
                                          if (openActionDropdown === employee.id) {
                                            setOpenActionDropdown(null);
                                          } else {
                                            setOpenActionDropdown(employee.id);
                                            setActionEmployee(employee);
                                            setDropdownPosition(calculateDropdownPosition(rect));
                                          }
                                        }}
                                        className={`w-8 h-8 rounded-full border border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] transition-colors duration-200 flex items-center justify-center mx-auto ${
                                          darkMode
                                            ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[#3C3C3C]"
                                            : "bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]"
                                        }`}
                                        title="Payroll actions"
                                      >
                                        <MoreVertical className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                  <td
                                    className="px-1 py-1 text-center align-middle"
                                    style={{
                                      width: `${columnWidths.select}px`,
                                      ...stickyTd(freezeLeft.select),
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={payrollSelectedEmployeeIds.includes(employee.id)}
                                      onChange={() => togglePayrollEmployeeSelected(employee.id)}
                                      aria-label={`Select ${employee.empCode}`}
                                    />
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm font-medium"
                                    style={{
                                      width: `${columnWidths.slNo}px`,
                                      ...stickyTd(freezeLeft.slNo),
                                    }}
                                  >
                                    {index + 1}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.empCode}px`,
                                      ...stickyTd(freezeLeft.empCode),
                                    }}
                                  >
                                    {employee.empCode}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm font-medium"
                                    style={{
                                      width: `${columnWidths.name}px`,
                                      ...stickyTd(freezeLeft.name),
                                    }}
                                  >
                                    {employee.user?.name}{" "}
                                    {employee.user?.lastName || ""}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.designation}px`,
                                    }}
                                  >
                                    {getNameByIdFromList(
                                      employee.designationId,
                                      designationsList
                                    )}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.department}px`,
                                    }}
                                  >
                                    {getNameByIdFromList(
                                      employee.departmentId,
                                      departmentsList
                                    )}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.joiningDate}px`,
                                    }}
                                  >
                                    {formatDisplayDate(
                                      employee.dateOfJoin ||
                                        employee.joiningDate ||
                                        employee.joinDate
                                    )}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.exitDate}px`,
                                    }}
                                  >
                                    {formatDisplayDate(getEmployeeRelievingDate(employee) || "-")}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm text-right"
                                    style={{ width: `${columnWidths.ctc}px` }}
                                  >
                                    {employee.ctc
                                      ? employee.ctc.toLocaleString()
                                      : "-"}
                                  </td>
                                  <td
                                    className="px-3 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.salaryStructure || 160}px`,
                                      minWidth: 140,
                                    }}
                                    title={
                                      payrollBreakdownByEmployee.get(employee.id)
                                        ?.structureName || ""
                                    }
                                  >
                                    {payrollBreakdownByEmployee.get(employee.id)
                                      ?.structureName || "—"}
                                  </td>
                                  {payrollComponentColumns.map((compName) => {
                                    const amt =
                                      payrollBreakdownByEmployee.get(employee.id)
                                        ?.amountsByName?.[compName];
                                    return (
                                      <td
                                        key={`cell-${employee.id}-${compName}`}
                                        className="px-3 py-2 whitespace-nowrap text-sm text-right tabular-nums"
                                        style={{ width: 120, minWidth: 110 }}
                                      >
                                        {formatPayrollAmount(amt)}
                                      </td>
                                    );
                                  })}
                                  <td
                                    className="px-3 py-2 whitespace-nowrap text-sm text-right tabular-nums font-medium"
                                    style={{ width: 120, minWidth: 110 }}
                                  >
                                    {formatPayrollAmount(
                                      payrollBreakdownByEmployee.get(employee.id)?.grossPay
                                    )}
                                  </td>
                                  <td
                                    className="px-3 py-2 whitespace-nowrap text-sm text-right tabular-nums font-semibold"
                                    style={{ width: 120, minWidth: 110 }}
                                  >
                                    {formatPayrollAmount(
                                      payrollBreakdownByEmployee.get(employee.id)?.netPay
                                    )}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap"
                                    style={{
                                      width: `${columnWidths.taxRegime}px`,
                                    }}
                                  >
                                    {(() => {
                                      const header = investmentHeaders[employee.id];
                                      const regimeRaw = header?.taxRegime || employee.taxRegime || "NEW";
                                      const regime = regimeRaw.toString().toUpperCase();
                                      const isOld = regime === "OLD";
                                      const label = isOld ? "Old" : "New";
                                      return (
                                        <span
                                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            isOld
                                              ? "rh-accent-soft"
                                              : "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                                          }`}
                                        >
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap"
                                    style={{
                                      width: `${columnWidths.status}px`,
                                    }}
                                  >
                                    {(() => {
                                      const header = investmentHeaders[employee.id];
                                      const rawStatus = header?.status;
                                      
                                      // If no status is available, show "Default"
                                      if (!rawStatus || rawStatus === null || rawStatus === undefined) {
                                        return (
                                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]">
                                            Default
                                          </span>
                                        );
                                      }
                                      
                                      const upper = rawStatus.toString().toUpperCase();
                                      let label = rawStatus;
                                      let cls = "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]";

                                      if (upper === "DRAFT") {
                                        label = "Draft";
                                        cls = "rh-accent-soft";
                                      } else if (upper === "APPROVED" || upper === "REVIEWED") {
                                        label = upper.charAt(0) + upper.slice(1).toLowerCase();
                                        cls = "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300";
                                      } else if (upper === "SUBMITTED") {
                                        label = upper.charAt(0) + upper.slice(1).toLowerCase();
                                        cls = "border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300";
                                      } else if (upper === "REJECTED") {
                                        label = "Rejected";
                                        cls = "bg-red-100 text-red-800";
                                      } else {
                                        // Fallback: keep raw text, neutral color
                                        label = rawStatus;
                                        cls = "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]";
                                      }

                                      return (
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${cls}`}>
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </td>

                                </tr>
                              ))}
                            </tbody>
                          </table>
                      </div>
                    </div>
                    )}

                    {/* Grid View */}
                    {viewMode === "grid" && (
                      <div className="min-h-0 w-full min-w-0 flex-1 overflow-x-auto overflow-y-auto">
                        <div className="grid grid-cols-4 gap-4 p-4">
                          {assignedStructureEmployees.map((employee, index) => (
                            <div
                              key={employee.id}
                              className={`p-3 rounded-none border ${
                                darkMode
                                  ? "bg-[#3C3C3C] border-[#4C4C4C] hover:bg-[#4C4C4C]"
                                  : "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                              } transition-colors cursor-pointer mb-2`}
                              onClick={() => handleViewEdit(employee, "view")}
                            >
                              <div className="space-y-1">
                                <div className="text-xs font-medium text-[hsl(var(--ds-fg))] dark:text-white truncate">
                                  {employee.empCode}
                                </div>
                                <div className="text-xs font-medium text-[hsl(var(--ds-fg))] dark:text-white truncate">
                                  {employee.user?.name}{" "}
                                  {employee.user?.lastName || ""}
                                </div>
                                <div className="text-xs text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))] truncate">
                                  {getNameByIdFromList(
                                    employee.designationId,
                                    designationsList
                                  )}
                                </div>
                                <div className="text-[11px] text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))] truncate">
                                  Structure:{" "}
                                  {payrollBreakdownByEmployee.get(employee.id)
                                    ?.structureName || "—"}
                                </div>
                                <div className="text-[11px] font-medium text-[hsl(var(--ds-fg))] dark:text-gray-200">
                                  Net Pay:{" "}
                                  {formatPayrollAmount(
                                    payrollBreakdownByEmployee.get(employee.id)?.netPay
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                  <div className="flex items-center space-x-1">
                                    {(() => {
                                      const header = investmentHeaders[employee.id];
                                      const regimeRaw = header?.taxRegime || employee.taxRegime || "NEW";
                                      const regime = regimeRaw.toString().toUpperCase();
                                      const isOld = regime === "OLD";
                                      return (
                                        <span
                                          className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                                            isOld
                                              ? "rh-accent-soft"
                                              : "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                                          }`}
                                        >
                                          {isOld ? "Old" : "New"}
                                        </span>
                                      );
                                    })()}
                                    {(() => {
                                      const header = investmentHeaders[employee.id];
                                      const rawStatus = header?.status;
                                      
                                      // If no status is available, show "Default"
                                      if (!rawStatus || rawStatus === null || rawStatus === undefined) {
                                        return (
                                          <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]">
                                            Default
                                          </span>
                                        );
                                      }
                                      
                                      const upper = rawStatus.toString().toUpperCase();
                                      let label = rawStatus;
                                      let cls = "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]";

                                      if (upper === "DRAFT") {
                                        label = "Draft";
                                        cls = "rh-accent-soft";
                                      } else if (upper === "APPROVED" || upper === "REVIEWED") {
                                        label = upper.charAt(0) + upper.slice(1).toLowerCase();
                                        cls = "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300";
                                      } else if (upper === "SUBMITTED") {
                                        label = upper.charAt(0) + upper.slice(1).toLowerCase();
                                        cls = "border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300";
                                      } else if (upper === "REJECTED") {
                                        label = "Rejected";
                                        cls = "bg-red-100 text-red-800";
                                      } else {
                                        label = rawStatus;
                                        cls = "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]";
                                      }

                                      return (
                                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${cls}`}>
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewEdit(employee, "view");
                                      }}
                                      className={`p-1 rounded-md transition-colors ${
                                        darkMode
                                          ? "text-[hsl(var(--ds-accent))] hover:text-white hover:bg-[#3C3C3C]"
                                          : "text-[hsl(var(--ds-secondary))] hover:text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-accent))]/50"
                                      }`}
                                      title="View"
                                    >
                                      <Eye className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewEdit(employee, "edit");
                                      }}
                                      className={`p-1 rounded-md transition-colors ${
                                        darkMode
                                          ? "text-[hsl(var(--ds-accent))] hover:bg-[#3C3C3C] hover:text-white"
                                          : "text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))]/50 hover:text-[hsl(var(--ds-primary))]"
                                      }`}
                                      title="Edit"
                                    >
                                      <Edit className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                 </div>
               )}

            {!isLoading && !fetchError && filteredEmployees.length > 0 && (
              <div
                className={`-mx-px w-[calc(100%+2px)] shrink-0 border border-t-0 px-3 py-2 ${
                  darkMode
                    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                }`}
              >
                <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md border px-2 py-1 text-xs font-semibold tabular-nums tracking-tight ${
                        darkMode
                          ? "border-[#4C4C4C] bg-[#252525] text-[hsl(var(--ds-fg))]"
                          : "border-[#d7dee8] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-sm"
                      }`}
                      title={
                        Number(totalEmployees) > 0
                          ? `Payroll scope: ${payrollEligibleEmployees.length} employee(s) eligible for ${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}. ${assignedStructureEmployees.length} row(s) visible in the grid. Employee module total (all filters): ${totalEmployees}.`
                          : `Payroll scope: ${payrollEligibleEmployees.length} employee(s) for this month. ${assignedStructureEmployees.length} row(s) visible in the grid.`
                      }
                    >
                      <span className="whitespace-nowrap">Payroll scope:</span>{" "}
                      <span className="font-bold">{payrollEligibleEmployees.length}</span>
                      {assignedStructureEmployees.length < payrollEligibleEmployees.length && (
                        <span
                          className={`whitespace-nowrap text-[10px] font-normal opacity-80 ${
                            darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                          }`}
                        >
                          ({assignedStructureEmployees.length} in grid)
                      </span>
                      )}
                      {Number(totalEmployees) > 0 &&
                        payrollEligibleEmployees.length !== Number(totalEmployees) && (
                        <span
                          className={`whitespace-nowrap text-[10px] font-normal opacity-80 ${
                              darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                          }`}
                        >
                            · Employee list: {totalEmployees}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 items-center justify-end">
                    <div className="flex min-w-0 items-center justify-end space-x-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                    {hasActiveFilters && (
                      <div
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                          darkMode
                            ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                            : "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                        }`}
                      >
                        Filtered
                      </div>
                    )}
                    <div
                      className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                        darkMode
                          ? "bg-[hsl(var(--ds-secondary))] text-[hsl(var(--ds-accent))]"
                          : "rh-accent-soft"
                      }`}
                    >
                      Old Regime: {oldRegimeCount}
                    </div>

                    <div
                      className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                        darkMode
                          ? "border border-emerald-800/50 bg-emerald-950/50 text-emerald-300"
                          : "border border-emerald-200 bg-emerald-50 text-emerald-900"
                      }`}
                    >
                      New Regime: {newRegimeCount}
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
              </div>
            </div>
          </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activePayrollTab === "reports" && canViewPayrollReports && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {false && (
            <ActionInsightsPanel
              darkMode={darkMode}
              title="Payroll Action Insights"
              items={payrollInsightItems}
              isFlipped={showPayrollInsights}
              onFlip={() => setShowPayrollInsights((v) => !v)}
              showFlipButton={false}
              overlayMode="sibling-hide"
            />
            )}
            {!showPayrollInsights && (
              activePayrollReportsSubTab === "salary-revision" ? (
                <Reports
                  key={`payroll-salary-revision-${activePayrollReportsSubTab}`}
                  darkMode={darkMode}
                  setCurrentPage={setCurrentPage}
                  toggleDarkMode={toggleDarkMode}
                  forcedSubTab="salary-revision"
                  hideSidebar
                  canDownload={canExportSalaryRevisionReport}
                />
              ) : activePayrollReportsSubTab === "unpaid-salary" ? (
                canViewUnpaidSalaryReport ? (
                  <div className="min-h-0 flex-1 overflow-auto px-2 pb-2 lg:px-4">
                    <UnpaidSalaryReportPanel darkMode={darkMode} />
                    {!canExportUnpaidSalaryReport && (
                      <p className="px-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                        Export requires Unpaid Salary export permission.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="p-4 text-sm text-[hsl(var(--ds-muted-fg))]">
                    You do not have access to Unpaid Salary report.
                  </p>
                )
              ) : (
            <PayrollReports
              key={`payroll-reports-${activePayrollReportsSubTab}`}
              darkMode={darkMode}
              setCurrentPage={setCurrentPage}
              toggleDarkMode={toggleDarkMode}
              forcedSubTab={activePayrollReportsSubTab}
              hideSidebar
            />
              )
            )}
          </div>
        )}

        {activePayrollTab === "statutory_filing" && canViewStatutoryFiling && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="shrink-0 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 pt-3">
              <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">Statutory Filing</h2>
              <p className="ds-caption mt-0.5 text-[hsl(var(--ds-muted-fg))]">
                PF, ESI, PT, NPS, Meal voucher remittance and Form 24Q filing
              </p>
              <div className="mt-3 flex flex-wrap gap-0" role="tablist" aria-label="Statutory filing returns">
                {[
                  { id: "pf", label: "PF" },
                  { id: "esi", label: "ESI" },
                  { id: "pt", label: "PT" },
                  { id: "nps", label: "NPS" },
                  { id: "meal", label: "Meal Voucher" },
                  ...(canViewForm24Q
                    ? [{ id: "form-24q", label: "e-TDS Return" }]
                    : []),
                ].map((item) => {
                  const active = activeStatutoryFilingSubTab === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => {
                        selectStatutoryFilingSubTab(item.id);
                      }}
                      className={`relative shrink-0 px-4 py-2.5 text-sm font-semibold transition ${
                        active
                          ? "text-[hsl(var(--ds-primary))]"
                          : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      {item.label}
                      {active ? (
                        <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[hsl(var(--ds-primary))]" />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <PayrollStatutoryFilingPanel
                key={`statutory-filing-${activeStatutoryFilingSubTab}`}
                activeSubTab={activeStatutoryFilingSubTab}
                darkMode={darkMode}
                canViewForm24Q={canViewForm24Q}
              />
            </div>
          </div>
        )}

        {activePayrollTab === "settings" && canOpenPayrollSettings && (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
            <PayrollSettingsPage
              darkMode={darkMode}
              employees={employees}
              payrollSettings={payrollSettings}
              investmentSettingsRef={investmentSettingsRef}
              onFlagsChange={handleInvestmentFlagsChange}
              onSettingsSaved={(data) => applyPayrollSettingsToState(data)}
              canEdit={canEditPayrollSettings}
              initialSub={searchParams.get("sub")}
              departmentsList={departmentsList}
              branchesList={branchesList}
              designationsList={designationsList}
              subDepartmentsList={subDepartmentsList}
              gradesList={gradesList}
              categoriesList={categoriesList}
            />
          </div>
        )}

          </div>
          </div>
        </div>

        {openActionDropdown && actionEmployee && (
          <div
            className={`fixed z-[9998] overflow-hidden rounded-lg border shadow-xl action-dropdown ${
              darkMode
                ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
            }`}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              minWidth: "200px",
            }}
          >
            <div
              className={`flex items-center justify-between border-b px-4 py-2.5 ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))]"
                  : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className={`text-xs font-semibold truncate ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                  {actionEmployee.empCode || actionEmployee.employeeId}
                </div>
                <div className={`text-xs truncate ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  {actionEmployee.user?.name} {actionEmployee.user?.lastName || ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpenActionDropdown(null)}
                className={`ml-2 rounded p-1 ${darkMode ? "text-red-400 hover:bg-[#3C3C3C]" : "text-red-500 hover:bg-red-50"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              {[
                ...(canReviseSalary ? [["salaryRevision", "Salary Revision"]] : []),
                ...(canUpdateStatutory
                  ? [["updateStatutory", "Update Statutory & Benefits Applicability"]]
                  : []),
                ["taxDeclaration", "Tax Planning"],
                ...(canLoan ? [["loan", "Loan"]] : []),
                ...(canLeaveEncashment ? [["leaveEncashment", "Leave Encashment"]] : []),
                ["advance", "Advance"],
                ["reimbursement", "Reimbursement"],
              ].map(([action, label], idx) => (
                <button
                  key={action}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePayrollRowAction(action, actionEmployee);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-xs font-medium transition-colors ${
                    idx > 0 ? "border-t" : ""
                  } ${
                    darkMode
                      ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[#3C3C3C]"
                      : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <InvestmentDeclarationModal
            employee={selectedEmployee}
            mode={modalMode}
            onClose={() => setIsModalOpen(false)}
            onSave={async (updatedData) => {
              console.log("Saving updated data:", updatedData);
              
              // Only close modal if it's a full submission, not for intermediate updates
              if (updatedData?.type === 'submitted' || updatedData?.type === 'submit_success') {
                setIsModalOpen(false);
              }
              
              // Reload investment headers to reflect updated status/regime/attachments
              await loadInvestmentHeaders();
            }}
            payrollSettings={payrollSettings}
            isInvestmentDeclarationEnabled={isInvestmentDeclarationEnabled()}
            isInvestmentProofEnabled={isInvestmentProofEnabled()}
            getInvestmentDeclarationMode={getInvestmentDeclarationMode}
            getInvestmentProofMode={getInvestmentProofMode}
            financialYear={selectedFinancialYear}
            payMonth={`${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}`}
            adminBypassWindows={modalMode === "edit"}
          />
        )}

        {/* Bulk Upload Investments Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div
              className={`rounded-none shadow-xl w-[700px] h-[600px] flex flex-col ${
                darkMode ? "bg-gray-800 text-white" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
              }`}
            >
              {/* Header - Fixed */}
              <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-[hsl(var(--ds-accent))]/50 to-gray-panel flex-shrink-0">
                <h2 className="text-xl font-bold text-[hsl(var(--ds-fg))]">
                  Bulk upload Investments
                </h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className={`${
                    darkMode
                      ? "text-[hsl(var(--ds-muted-fg))] hover:text-white"
                      : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-grow overflow-y-auto p-6 bg-[hsl(var(--ds-muted))]">
                <div className="space-y-4">
                  <p
                    className={`text-sm ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    Download the unified template with separate columns for Old
                    and New tax regimes:
                  </p>

                  <div className="space-y-3">
                    {/* Unified Template */}
                    <div
                      className={`p-4 rounded-none border ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))]"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold mb-2">
                            Unified Investment Declaration Template
                          </h3>
                          <p
                            className={`text-sm ${
                              darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                            }`}
                          >
                            Download template with separate columns for Old and
                            New tax regimes. Fill Old Regime columns if Tax
                            Regime = Old, fill New Regime columns if Tax Regime
                            = New.
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            handleTemplateDownload();
                            setShowTemplateModal(false);
                          }}
                          className={`px-4 py-2 text-sm font-medium rounded-none ${
                            darkMode
                              ? "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                              : "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                          } transition-colors`}
                        >
                          Download Unified Template
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div
                    className={`p-4 rounded-none ${
                      darkMode
                        ? "bg-[hsl(var(--ds-secondary))]/30 border border-[hsl(var(--ds-secondary))]"
                        : "bg-[hsl(var(--ds-accent))]/40 border border-[hsl(var(--ds-border))]"
                    }`}
                  >
                    <h4 className="font-semibold mb-2 text-[hsl(var(--ds-secondary))]">
                      Instructions:
                    </h4>
                    <ul
                      className={`text-sm space-y-1 ${
                        darkMode ? "text-[hsl(var(--ds-accent))]" : "text-[hsl(var(--ds-secondary))]"
                      }`}
                    >
                      <li>
                        • Fill in the employee details and select Tax Regime
                        (Old/New)
                      </li>
                      <li>
                        • If Tax Regime = Old: Fill only the "(Old)" columns,
                        leave "(New)" columns empty
                      </li>
                      <li>
                        • If Tax Regime = New: Fill only the "(New)" columns,
                        leave "(Old)" columns empty
                      </li>
                      <li>
                        • Save the file and use Upload Filled Template below to
                        upload
                      </li>
                    </ul>
                  </div>

                  {/* File Upload Section */}
                  <div
                    className={`p-4 rounded-none border ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))]"
                    }`}
                  >
                    <h4 className="font-semibold mb-3">
                      Upload Filled Template
                    </h4>
                    <label
                      className={`block w-full px-4 py-3 border-2 border-dashed rounded-none cursor-pointer transition-colors ${
                        darkMode
                          ? "border-gray-500 hover:border-gray-400 hover:bg-gray-600"
                          : "border-[hsl(var(--ds-border))] hover:border-gray-400 hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          handleBulkImport(e);
                          setShowTemplateModal(false);
                        }}
                        accept=".csv,.xlsx,.xls"
                      />
                      <div className="text-center">
                        <FileUp className="mx-auto h-8 w-8 mb-2 text-[hsl(var(--ds-muted-fg))]" />
                        <p
                          className={`text-sm ${
                            darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                          }`}
                        >
                          Click to select Excel file (.xlsx, .xls) or CSV file
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                          }`}
                        >
                          Supported formats: .xlsx, .xls, .csv
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer Action Buttons - Fixed */}
              <div className="border-t p-6 bg-[hsl(var(--ds-surface))] flex justify-end space-x-3 flex-shrink-0">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-none ${
                    darkMode
                      ? "bg-gray-600 text-white hover:bg-[hsl(var(--ds-muted-fg))]"
                      : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-gray-300"
                  } transition-colors`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Download Confirmation Modal */}
        {showDownloadConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
            <div
              className={`p-6 rounded-lg shadow-xl max-w-md w-full mx-4 ${
                darkMode
                  ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                  : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"
                  }`}
                >
                  Download Warning
                </h3>
                <button
                  onClick={() => setShowDownloadConfirm(false)}
                  className={`${
                    darkMode
                      ? "text-[hsl(var(--ds-muted-fg))] hover:text-white"
                      : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                  } transition-colors`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                      darkMode ? "bg-amber-950/80" : "bg-amber-100"
                    }`}
                  >
                    <span className="text-lg text-amber-600 dark:text-amber-400">⚠️</span>
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-sm ${
                        darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      Only <span className="font-semibold">Old Regime</span> investment
                      declarations will be downloaded.
                    </p>
                    <p
                      className={`text-sm mt-2 ${
                        darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      New Regime data will not be included in this export.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDownloadConfirm(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-none transition-colors ${
                    darkMode
                      ? "bg-gray-700 text-[hsl(var(--ds-fg))] hover:bg-gray-600"
                      : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-gray-300"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDownload}
                  className={`px-4 py-2 text-sm font-medium rounded-none transition-colors ${
                    darkMode
                      ? "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                      : "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                  }`}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters modal */}
        {showFilterPopup && (
          <Filters
            onApply={(_employees, formFilters, params) => {
              // Use params (API-formatted keys) for backend queries
              setAppliedFilters(params || {});
              // Persist last-applied form state so it "stays" until Clear + Apply
              setAppliedFiltersForm(formFilters || {});
              setShowFilterPopup(false);
            }}
            onClose={() => setShowFilterPopup(false)}
            darkMode={darkMode}
            initialFilters={appliedFiltersForm}
          />
        )}

        <AttachmentPreviewModal
          isOpen={incomeTaxGuidePreview.isOpen}
          url={incomeTaxGuidePreview.url}
          downloadUrl={incomeTaxGuidePreview.downloadUrl}
          fileName={incomeTaxGuidePreview.fileName}
          onClose={() =>
            {
              if (incomeTaxGuideBlobUrlRef.current) {
                URL.revokeObjectURL(incomeTaxGuideBlobUrlRef.current);
                incomeTaxGuideBlobUrlRef.current = null;
              }
              setIncomeTaxGuidePreview({
                isOpen: false,
                url: null,
                downloadUrl: null,
                fileName: "",
              });
            }
          }
        />

        <PayrollSalaryRevisionModal
          isOpen={!!revisionEmployee}
          onClose={() => setRevisionEmployee(null)}
          employee={revisionEmployee}
          structures={salaryStructures}
          statutoryConfigJson={statutoryConfigJson}
          branchesList={branchesList}
          darkMode={darkMode}
          allowMidMonthIncrements={!!payrollSettings?.allowMidMonthIncrements}
          bookingPayMonth={`${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}`}
          onSaved={() => {
            setMountTrigger((p) => p + 1);
            setStatutoryOverridesByEmployeeId(new Map(statutoryOverridesByEmployeeId));
          }}
        />

        {structureEmployee ? (
          <EmployeeSalaryStructureModal
            employee={structureEmployee}
            darkMode={darkMode}
            orgSalaryMethod={orgSalaryMethod}
            autoOpenKey={structureAutoOpenKey}
            hideTrigger
            onSaved={() => {
              setMountTrigger((p) => p + 1);
            }}
          />
        ) : null}

        <SalaryRevisionImportModal
          isOpen={salaryRevisionImportOpen}
          onClose={() => setSalaryRevisionImportOpen(false)}
          darkMode={darkMode}
          employees={filteredEmployees}
          structures={salaryStructures}
          statutoryConfigJson={statutoryConfigJson}
          branchesList={branchesList}
          allowMidMonthIncrements={!!payrollSettings?.allowMidMonthIncrements}
          bookingPayMonth={`${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}`}
          onImported={() => {
            setMountTrigger((p) => p + 1);
          }}
        />

        <LoanEmployeeModal
          isOpen={!!loanEmployee}
          onClose={() => setLoanEmployee(null)}
          employee={loanEmployee}
          darkMode={darkMode}
        />
        {flexiClaimsOpen ? (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
            <div
              className={`max-h-[90vh] w-full max-w-5xl overflow-auto rounded-xl border p-4 shadow-xl ${
                darkMode
                  ? "border-slate-700 bg-slate-900 text-slate-100"
                  : "border-slate-200 bg-white text-slate-900"
              }`}
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">Reimbursement Control Center</h3>
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-xs opacity-70 hover:opacity-100"
                  onClick={() => setFlexiClaimsOpen(false)}
                >
                  Close
                </button>
              </div>
              <AdminFlexiClaimsPanel
                darkMode={darkMode}
                payMonth={`${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}`}
              />
            </div>
          </div>
        ) : null}
        <LeaveEncashmentEmployeeModal
          isOpen={!!encashEmployee}
          onClose={() => setEncashEmployee(null)}
          employee={encashEmployee}
          darkMode={darkMode}
        />
        {/* Process F&F opens inline inside PayrollActionsHub (keeps nav/sidebars). */}

        <StructureComponentImportModal
          isOpen={structureComponentImportOpen}
          onClose={() => setStructureComponentImportOpen(false)}
          darkMode={darkMode}
          employees={filteredEmployees}
          structures={salaryStructures}
          statutoryConfigJson={statutoryConfigJson}
          branchesList={branchesList}
          onImported={() => {
            setMountTrigger((p) => p + 1);
          }}
        />

        {/* Legacy stub — Bulk Import sidebar item removed; ref retained for HMR safety */}
        <input ref={bulkImportInputRef} type="file" className="hidden" tabIndex={-1} aria-hidden="true" />

        <UpdateStatutoryApplicabilityModal
          isOpen={!!statutoryEmployee}
          onClose={() => setStatutoryEmployee(null)}
          employee={statutoryEmployee}
          structures={salaryStructures}
          statutoryConfigJson={statutoryConfigJson}
          branchesList={branchesList}
          darkMode={darkMode}
          onSaved={() => {
            const asOf = `${selectedYear}-${String(Number(selectedMonth) + 1).padStart(2, "0")}`;
            const ids = (filteredEmployees || []).map((e) => e.id).filter(Boolean);
            payrollOpsService
              .resolveStatutoryBatch(ids, asOf)
              .then((res) => {
                const raw = res?.data || {};
                const map = new Map();
                Object.entries(raw).forEach(([k, v]) => {
                  map.set(Number(k), v);
                  map.set(String(k), v);
                });
                setStatutoryOverridesByEmployeeId(map);
              })
              .catch(() => {});
            setMountTrigger((p) => p + 1);
          }}
        />
      </div>
    </div>
  );
}
